/**
 * @fileoverview Own administrator editing of Intake agents, prompts, pipeline versions, model profiles, staff routing, and operational settings. `kc-internal-frontend/src/components/Intake/intakeApi.js` owns HTTP contracts; backend version/publication rules determine accepted configuration. `IntakePipelineOrchestrationMap.js` visualizes the selected pipeline. Local forms, published versions, active execution, and operational health are distinct state. Saving configuration does not stand in for reviewing a lead or running its pipeline.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ToastContext from '../../ToastContext';
import { AppButton, AppModal, AppTab, AppTabView } from '../shared';
import { AdminCard, AdminEmptyState, AdminPage, AdminSection } from './AdminChrome';
import {
    archiveIntakeGlobalPromptVersion,
    archiveIntakePromptVersion,
    archiveIntakePipelineVersion,
    createIntakeAgent,
    createIntakeGlobalPromptVersion,
    createIntakePipelineVersion,
    createIntakePromptVersion,
    createIntakeStaffRole,
    createIntakeStaffRoleAssignment,
    fetchIntakeAdminMetrics,
    fetchIntakeAgents,
    fetchIntakeAttorneyRoutingProfiles,
    fetchIntakeGlobalPromptVersions,
    fetchIntakeModelProfiles,
    fetchIntakeOperationalSettings,
    fetchIntakePipelineVersion,
    fetchIntakePipelineVersions,
    fetchIntakePromptVersions,
    fetchIntakeStaffRoles,
    publishIntakeGlobalPromptVersion,
    publishIntakePipelineVersion,
    publishIntakePromptVersion,
    updateIntakeAgent,
    updateIntakeAttorneyRoutingProfile,
    updateIntakeGlobalPromptVersion,
    updateIntakeOperationalSettings,
    updateIntakePipelineVersion,
    updateIntakePromptVersion,
    updateIntakeStaffRole,
    updateIntakeStaffRoleAssignment,
} from '../Intake/intakeApi';
import { formatDate, STAGE_OPTIONS, titleize } from '../Intake/intakeFormatters';
import '../Intake/Intake.css';

const IntakePipelineOrchestrationMap = React.lazy(() => import('./IntakePipelineOrchestrationMap'));

const MODEL_TIER_OPTIONS = [
    ['mini', 'Mini'],
    ['medium', 'Medium'],
    ['flagship', 'Flagship'],
    ['fallback', 'Fallback'],
];

const ASSIGNEE_TYPES = [
    ['employee', 'Employee'],
    ['attorney', 'Attorney'],
    ['external', 'External'],
];

const OPERATIONAL_SETTING_DEFAULTS = {
    intake_outreach_delivery_mode: 'dry_run',
    intake_outreach_redirect_email: '',
    intake_outreach_sender_email: '',
    intake_upload_link_mode: 'external',
    intake_dev_upload_simulator_enabled: false,
    file_uploader_base_url: 'https://upload.kneuppercovey.com',
    intake_dev_upload_base_url: 'http://localhost:5100',
    information_request_followups_enabled: true,
    information_request_followup_interval_days: 2,
    information_request_great_case_max_followups: 3,
    information_request_ok_case_max_followups: 1,
    intake_batch_window_days: 4,
    intake_batch_max_leads: 100,
    intake_batch_outreach_limit: 10,
    information_request_minimum_quality_score: 70,
    intake_stale_archive_days: 30,
    intake_email_acquisition_role_key: 'intake_reviewer',
};

const OUTREACH_DELIVERY_OPTIONS = [
    ['dry_run', 'Dry run'],
    ['redirect', 'Redirect to tester'],
    ['live', 'Live'],
];

const UPLOAD_LINK_MODE_OPTIONS = [
    ['dev_internal', 'Dev simulator'],
    ['external', 'Production uploader'],
];

const EMPTY_ROLE_FORM = {
    id: null,
    role_key: '',
    display_name: '',
    description: '',
    active: true,
};

const EMPTY_AGENT_FORM = {
    key: '',
    display_name: '',
    description: '',
    stage_key: 'triage',
    default_model_tier: 'mini',
    active: true,
};

const EMPTY_PROMPT_MODEL_SETTINGS = {
    temperature: '',
    max_tokens: '',
    reasoning_effort: '',
};

const REASONING_EFFORT_OPTIONS = [
    ['', 'Default'],
    ['low', 'Low'],
    ['medium', 'Medium'],
    ['high', 'High'],
];

const CORE_STAGE_KEYS = ['ingest_dedupe', 'triage', 'analysis', 'info_gathering', 'document_review', 'final_brief', 'decision', 'conversion'];
const STAGE_TYPE_OPTIONS = [
    ['ingest', 'Ingest'],
    ['routing', 'Routing'],
    ['enrichment', 'Enrichment'],
    ['drafting', 'Drafting'],
    ['review_gate', 'Review Gate'],
    ['decision', 'Decision'],
    ['conversion', 'Conversion'],
];
const DEFAULT_STAGE_POLICY = {
    ingest_dedupe: { stage_type: 'ingest', can_request_review_gate: false },
    triage: { stage_type: 'routing', can_request_review_gate: true },
    specialist_intake_review: { stage_type: 'review_gate', can_request_review_gate: true },
    class_action_detection: { stage_type: 'enrichment', can_request_review_gate: false },
    info_gathering: { stage_type: 'drafting', can_request_review_gate: false },
    information_request: { stage_type: 'drafting', can_request_review_gate: false },
    document_review: { stage_type: 'enrichment', can_request_review_gate: false },
    information_evaluation: { stage_type: 'review_gate', can_request_review_gate: true },
    specialist_evaluation: { stage_type: 'review_gate', can_request_review_gate: true },
    final_brief: { stage_type: 'decision', can_request_review_gate: true },
    decision: { stage_type: 'decision', can_request_review_gate: true },
    conversion: { stage_type: 'conversion', can_request_review_gate: false },
};
const AGENT_ROLE_OPTIONS = [
    ['specialist', 'Specialist'],
    ['dedupe', 'Dedupe'],
    ['triage', 'Triage'],
    ['information_request', 'Information Request'],
    ['information_evaluation', 'Information Evaluation'],
    ['document_review', 'Document Review'],
    ['routing_assignment', 'Routing/Assignment'],
    ['jurisdiction_arbitration', 'Jurisdiction/Arbitration'],
    ['claim_specialist', 'Claim Specialist'],
    ['defendant_evaluation', 'Defendant Evaluation'],
    ['research', 'Research'],
    ['final_eval', 'Final Evaluation'],
];
const FINAL_EVAL_AGENT_ROLES = new Set(['final_eval', 'final_evaluation', 'final_recommendation']);
const FIRM_CONTEXT_PROMPT_KEY = 'firm_context';
const CURRENT_CLASS_ACTIONS_PROMPT_KEY = 'current_class_actions';

const STAGE_DESCRIPTIONS = {
    ingest_dedupe: 'Lead received, duplicate checks, source data normalized.',
    triage: 'Category, jurisdiction, and basic eligibility signals.',
    analysis: 'Claim analysis and risk review notes.',
    info_gathering: 'Missing information and prospect outreach.',
    document_review: 'Requested documents and review status.',
    final_brief: 'Attorney-facing summary prepared for decision.',
    decision: 'Accept, reject, duplicate, or archive.',
    conversion: 'Accepted lead becomes a matter.',
};

const EMPTY_ASSIGNMENT_FORM = {
    id: null,
    role_id: '',
    assignee_email: '',
    assignee_type: 'employee',
    category: '',
    state: '',
    source: '',
    priority: 100,
    is_primary: false,
    active: true,
};



function FormField({ label, children, wide = false }) {
    return (
        <label className={`intake-form-field${wide ? ' intake-form-field--wide' : ''}`}>
            <span>{label}</span>
            {children}
        </label>
    );
}



function TextInput({ label, value, onChange, type = 'text', wide = false, list, disabled = false }) {
    return (
        <FormField label={label} wide={wide}>
            <input type={type} value={value || ''} list={list} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
        </FormField>
    );
}



function TextArea({ label, value, onChange, rows = 5, wide = true, disabled = false, readOnly = false, inputRef, onClick, onFocus, title }) {
    return (
        <FormField label={label} wide={wide}>
            <textarea
                ref={inputRef}
                rows={rows}
                value={value || ''}
                disabled={disabled}
                readOnly={readOnly}
                title={title}
                onClick={onClick}
                onFocus={onFocus}
                onChange={(event) => {
                    if (!readOnly) onChange(event.target.value);
                }}
            />
        </FormField>
    );
}



function SelectInput({ label, value, onChange, options, wide = false, disabled = false }) {
    return (
        <FormField label={label} wide={wide}>
            <select value={value || ''} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
                {options.map(([optionValue, optionLabel]) => (
                    <option key={optionValue} value={optionValue}>{optionLabel}</option>
                ))}
            </select>
        </FormField>
    );
}



function CheckboxInput({ label, checked, onChange, disabled = false }) {
    return (
        <label className="intake-checkbox-row intake-checkbox-row--boxed">
            <input type="checkbox" checked={Boolean(checked)} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
            <span>{label}</span>
        </label>
    );
}



function StatTile({ label, value, detail, tone = 'info' }) {
    return (
        <div className={`intake-decision-preview intake-badge--${tone}`}>
            <span>{label}</span>
            <strong>{value}</strong>
            {detail ? <small>{detail}</small> : null}
        </div>
    );
}



function statusClass(value) {
    if (value === 'published' || value === true || value === 'active') return 'intake-badge--success';
    if (value === 'draft') return 'intake-badge--warning';
    if (value === 'archived' || value === false || value === 'inactive') return 'intake-badge--muted';
    return 'intake-badge--info';
}



function StatusBadge({ value }) {
    return <span className={`intake-badge ${statusClass(value)}`}>{typeof value === 'boolean' ? (value ? 'Active' : 'Inactive') : titleize(value)}</span>;
}



function settingsArrayToValues(settings = []) {
    return settings.reduce((accumulator, setting) => {
        accumulator[setting.key] = setting.value ?? OPERATIONAL_SETTING_DEFAULTS[setting.key] ?? '';
        return accumulator;
    }, { ...OPERATIONAL_SETTING_DEFAULTS });
}



function getSettingSource(settings = [], key) {
    const row = settings.find((setting) => setting.key === key);
    return row?.source ? titleize(row.source) : 'Default';
}



function getOutreachDeliveryModeLabel(policy = {}) {
    const mode = String(policy.mode || '').toLowerCase();
    if (mode === 'redirect') return 'Redirect';
    if (mode === 'dry_run') return 'Dry Run';
    if (mode === 'live') return 'Live';
    return 'Unknown';
}



function getOutreachDeliveryTone(policy = {}, delivery = {}) {
    const mode = String(policy.mode || '').toLowerCase();
    const requiresSmtp = mode === 'live' || mode === 'redirect';
    if (policy.configured === false || delivery.sender_email_configured === false || (requiresSmtp && delivery.mailer_configured === false)) return 'danger';
    if (mode === 'redirect' || mode === 'dry_run') return 'warning';
    if (mode === 'live') return 'success';
    return 'info';
}



function getOutreachDeliveryDetail(policy = {}, delivery = {}) {
    const mode = String(policy.mode || '').toLowerCase();
    const requiresSmtp = mode === 'live' || mode === 'redirect';
    if (requiresSmtp && delivery.mailer_configured === false) return 'SMTP password missing';
    if (mode === 'redirect') return policy.redirect_email ? `Dev email: ${policy.redirect_email}` : 'Dev email missing';
    if (mode === 'dry_run') return policy.redirect_email ? `Preview: ${policy.redirect_email}` : 'No email is sent';
    if (mode === 'live') return delivery.sender_email ? `Sender: ${delivery.sender_email}` : 'Sender missing';
    return 'Delivery mode unavailable';
}



function getOutreachDeliveryNotice(policy = {}, delivery = {}) {
    if (delivery.sender_email_configured === false) {
        return {
            title: 'Outreach Sender Missing',
            detail: 'INTAKE_OUTREACH_SENDER_EMAIL is not configured, so email sends will fail.',
            tone: 'danger',
        };
    }
    if (policy.configured === false) {
        return {
            title: 'Outreach Delivery Misconfigured',
            detail: policy.error || 'The outreach delivery override is not configured correctly.',
            tone: 'danger',
        };
    }
    const mode = String(policy.mode || '').toLowerCase();
    if ((mode === 'live' || mode === 'redirect') && delivery.mailer_configured === false) {
        return {
            title: 'Outreach SMTP Password Missing',
            detail: delivery.mailer_error || 'INTAKE_MAILER_SMTP_PASSWORD is required to send Intake outreach email.',
            tone: 'danger',
        };
    }
    if (mode === 'redirect') {
        return {
            title: 'Outreach Redirect Active',
            detail: `Lead outreach sends only to ${policy.redirect_email || policy.delivered_recipient_email}; prospects do not receive it.`,
            tone: 'locked',
        };
    }
    if (mode === 'dry_run') {
        return {
            title: 'Outreach Dry Run Active',
            detail: 'Lead outreach is recorded for pipeline tracking, but no email is sent to prospects.',
            tone: 'locked',
        };
    }
    return null;
}



function submissionSettingsFromOperationalForm(form) {
    const { intake_outreach_sender_email: _fixedSender, ...settings } = form || {};
    return settings;
}



function parsePromptModelSettings(value) {
    if (!value) return { ...EMPTY_PROMPT_MODEL_SETTINGS };
    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { ...EMPTY_PROMPT_MODEL_SETTINGS };
        return {
            temperature: parsed.temperature ?? '',
            max_tokens: parsed.max_tokens ?? parsed.max_output_tokens ?? '',
            reasoning_effort: parsed.reasoning_effort || '',
        };
    } catch (error) {
        return { ...EMPTY_PROMPT_MODEL_SETTINGS };
    }
}



function serializePromptModelSettings(settings) {
    const payload = {};
    if (settings.temperature !== '' && settings.temperature !== null && settings.temperature !== undefined) {
        const temperature = Number(settings.temperature);
        if (!Number.isNaN(temperature)) payload.temperature = temperature;
    }
    if (settings.max_tokens !== '' && settings.max_tokens !== null && settings.max_tokens !== undefined) {
        const maxTokens = Number(settings.max_tokens);
        if (!Number.isNaN(maxTokens)) payload.max_tokens = maxTokens;
    }
    if (settings.reasoning_effort) {
        payload.reasoning_effort = settings.reasoning_effort;
    }
    return JSON.stringify(payload);
}



function buildPromptPayload(form) {
    return {
        system_prompt: form.system_prompt,
        user_prompt_template: form.user_prompt_template,
        variables_schema_json: form.variables_schema_json,
        output_schema_json: form.output_schema_json,
        model_tier: form.model_tier,
        model_settings_json: serializePromptModelSettings(form.model_settings),
        change_notes: form.change_notes,
    };
}



function validateJsonObject(value, label) {
    if (!value || !String(value).trim()) return '';
    try {
        const parsed = JSON.parse(value);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return `${label} must be a JSON object.`;
        return '';
    } catch (error) {
        return `${label} is not valid JSON.`;
    }
}



function getPromptJsonErrors(form) {
    return [
        validateJsonObject(form.variables_schema_json, 'Variables schema'),
        validateJsonObject(form.output_schema_json, 'Output schema'),
    ].filter(Boolean);
}



function parseJsonObjectValue(value) {
    if (!value || !String(value).trim()) return { parsed: null, error: '' };
    try {
        const parsed = JSON.parse(value);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return { parsed: null, error: 'JSON must be an object.' };
        }
        return { parsed, error: '' };
    } catch (error) {
        return { parsed: null, error: 'Invalid JSON.' };
    }
}



function formatJsonObject(value) {
    const { parsed, error } = parseJsonObjectValue(value);
    if (error || !parsed) return value || '';
    return JSON.stringify(parsed, null, 2);
}



function getSchemaPropertyNames(value) {
    const { parsed } = parseJsonObjectValue(value);
    if (!parsed?.properties || typeof parsed.properties !== 'object' || Array.isArray(parsed.properties)) return [];
    return Object.keys(parsed.properties);
}



function getSchemaWarnings(value, title) {
    const { parsed, error } = parseJsonObjectValue(value);
    if (error || !parsed) return [];
    const warnings = [];
    if (!parsed.type) warnings.push(`${title} does not declare a root type.`);
    if (parsed.type && parsed.type !== 'object' && !(Array.isArray(parsed.type) && parsed.type.includes('object'))) {
        warnings.push(`${title} root type is not object.`);
    }
    if (!parsed.properties || typeof parsed.properties !== 'object' || Array.isArray(parsed.properties) || !Object.keys(parsed.properties).length) {
        warnings.push(`${title} has no properties defined.`);
    }
    if (parsed.required && !Array.isArray(parsed.required)) {
        warnings.push(`${title} required must be an array.`);
    }
    return warnings;
}



function getSchemaSummary(value) {
    const { parsed, error } = parseJsonObjectValue(value);
    if (error || !parsed) return [];
    const properties = getSchemaPropertyNames(value);
    const required = Array.isArray(parsed.required) ? parsed.required : [];
    const rootType = Array.isArray(parsed.type) ? parsed.type.join(', ') : parsed.type || 'Unspecified';
    return [
        ['Root Type', rootType],
        ['Fields', properties.length ? `${properties.length}: ${properties.slice(0, 6).join(', ')}${properties.length > 6 ? ', ...' : ''}` : 'None'],
        ['Required', required.length ? required.slice(0, 6).join(', ') + (required.length > 6 ? ', ...' : '') : 'None'],
    ];
}



function extractTemplateVariables(template) {
    const variables = new Set();
    const matcher = /{{\s*([a-zA-Z_][\w.:-]*)\s*}}/g;
    let match = matcher.exec(template || '');
    while (match) {
        variables.add(match[1]);
        match = matcher.exec(template || '');
    }
    return Array.from(variables).sort();
}



function getTemplateVariableAnalysis(template, schemaJson) {
    const variables = extractTemplateVariables(template);
    const schemaProperties = getSchemaPropertyNames(schemaJson);
    const schemaPropertySet = new Set(schemaProperties);
    const variableRoots = new Set(variables.map((variable) => variable.split('.')[0]));
    return {
        variables,
        schemaProperties,
        missing: variables.filter((variable) => !schemaPropertySet.has(variable) && !schemaPropertySet.has(variable.split('.')[0])),
        unused: schemaProperties.filter((property) => !variableRoots.has(property)),
    };
}



function getAgentFormFromAgent(agent) {
    if (!agent) return { display_name: '', description: '', stage_key: 'triage', default_model_tier: 'mini', active: true };
    return {
        display_name: agent.display_name || '',
        description: agent.description || '',
        stage_key: agent.stage_key || 'triage',
        default_model_tier: agent.default_model_tier || 'mini',
        active: agent.active !== false,
    };
}



function getPromptFormFromVersion(version, selectedAgent) {
    if (!version) {
        return {
            system_prompt: '',
            user_prompt_template: '',
            variables_schema_json: '',
            output_schema_json: '',
            model_tier: selectedAgent?.default_model_tier || 'mini',
            model_settings: { ...EMPTY_PROMPT_MODEL_SETTINGS },
            change_notes: '',
        };
    }
    return {
        system_prompt: version.system_prompt || '',
        user_prompt_template: version.user_prompt_template || '',
        variables_schema_json: version.variables_schema_json || '',
        output_schema_json: version.output_schema_json || '',
        model_tier: version.model_tier || selectedAgent?.default_model_tier || 'mini',
        model_settings: parsePromptModelSettings(version.model_settings_json),
        change_notes: version.change_notes || '',
    };
}



function buildPromptTextFromForm(form) {
    return [
        'Model Tier:',
        form.model_tier || '',
        '',
        'Model Settings:',
        serializePromptModelSettings(form.model_settings || {}),
        '',
        'System:',
        form.system_prompt || '',
        '',
        'User:',
        form.user_prompt_template || '',
        '',
        'Variables Schema:',
        formatJsonObject(form.variables_schema_json || '{}'),
        '',
        'Output Schema:',
        formatJsonObject(form.output_schema_json || '{}'),
    ].join('\n');
}



function buildPromptTextFromVersion(version) {
    if (!version) return '';
    return buildPromptTextFromForm(getPromptFormFromVersion(version));
}



function buildAssembledPromptText(firmContextPrompt, promptForm) {
    return [
        'Firm Context:',
        firmContextPrompt?.prompt_text || 'No published firm context',
        '',
        'System:',
        promptForm.system_prompt || '',
        '',
        'User:',
        promptForm.user_prompt_template || '',
    ].join('\n');
}



function buildLineDiff(leftText, rightText) {
    const left = String(leftText || '').split('\n');
    const right = String(rightText || '').split('\n');
    if (!String(leftText || '').trim() && !String(rightText || '').trim()) return [];
    const matrix = Array.from({ length: left.length + 1 }, () => Array(right.length + 1).fill(0));
    for (let leftIndex = left.length - 1; leftIndex >= 0; leftIndex -= 1) {
        for (let rightIndex = right.length - 1; rightIndex >= 0; rightIndex -= 1) {
            matrix[leftIndex][rightIndex] = left[leftIndex] === right[rightIndex]
                ? matrix[leftIndex + 1][rightIndex + 1] + 1
                : Math.max(matrix[leftIndex + 1][rightIndex], matrix[leftIndex][rightIndex + 1]);
        }
    }
    const rows = [];
    let leftIndex = 0;
    let rightIndex = 0;
    while (leftIndex < left.length && rightIndex < right.length) {
        if (left[leftIndex] === right[rightIndex]) {
            rows.push({ type: 'same', text: left[leftIndex] });
            leftIndex += 1;
            rightIndex += 1;
        } else if (matrix[leftIndex + 1][rightIndex] >= matrix[leftIndex][rightIndex + 1]) {
            rows.push({ type: 'removed', text: left[leftIndex] });
            leftIndex += 1;
        } else {
            rows.push({ type: 'added', text: right[rightIndex] });
            rightIndex += 1;
        }
    }
    while (leftIndex < left.length) {
        rows.push({ type: 'removed', text: left[leftIndex] });
        leftIndex += 1;
    }
    while (rightIndex < right.length) {
        rows.push({ type: 'added', text: right[rightIndex] });
        rightIndex += 1;
    }
    return rows;
}



function SchemaEditor({ title, value, onChange, disabled, validationMessages = [] }) {
    const error = validateJsonObject(value, title);
    const warnings = error ? [] : getSchemaWarnings(value, title);
    const summary = error ? [] : getSchemaSummary(value);
    const canFormat = !disabled && value && !error;
    return (
        <details className="intake-stage-context intake-schema-editor">
            <summary>{title}</summary>
            <div className="intake-schema-editor__toolbar">
                <div className="intake-schema-editor__summary">
                    {summary.map(([label, detail]) => (
                        <span key={label}><strong>{label}</strong>{detail}</span>
                    ))}
                </div>
                <button type="button" className="intake-button" disabled={!canFormat} onClick={() => onChange(formatJsonObject(value))}>Format JSON</button>
            </div>
            <textarea rows={8} value={value || ''} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
            {error ? <div className="intake-error">{error}</div> : null}
            {warnings.map((warning) => <div className="intake-schema-editor__warning" key={warning}>{warning}</div>)}
            {validationMessages.map((message) => <div className="intake-schema-editor__warning" key={message}>{message}</div>)}
            {!value ? <AdminEmptyState title="No schema configured" /> : null}
        </details>
    );
}



function UnsavedBadge({ visible }) {
    if (!visible) return null;
    return <span className="intake-badge intake-badge--warning">Unsaved changes</span>;
}



function VariableChipList({ values, emptyLabel, tone = 'info' }) {
    if (!values.length) return <span className="intake-template-variable-panel__empty">{emptyLabel}</span>;
    return (
        <div className="intake-template-variable-panel__chips">
            {values.map((value) => <span className={`intake-badge intake-badge--${tone}`} key={value}>{value}</span>)}
        </div>
    );
}



function TemplateVariablePanel({ template, schemaJson }) {
    const analysis = getTemplateVariableAnalysis(template, schemaJson);
    return (
        <div className="intake-template-variable-panel">
            <div className="intake-template-variable-panel__header">
                <strong>Template Variables</strong>
                <span>{analysis.variables.length} referenced / {analysis.schemaProperties.length} schema fields</span>
            </div>
            <div className="intake-template-variable-panel__grid">
                <div>
                    <span>Referenced</span>
                    <VariableChipList values={analysis.variables} emptyLabel="None" tone="info" />
                </div>
                <div>
                    <span>Missing from Schema</span>
                    <VariableChipList values={analysis.missing} emptyLabel="None" tone={analysis.missing.length ? 'danger' : 'success'} />
                </div>
                <div>
                    <span>Unused Schema Fields</span>
                    <VariableChipList values={analysis.unused} emptyLabel="None" tone="muted" />
                </div>
            </div>
        </div>
    );
}



function PromptTextPanel({ title, version, text, emptyTitle = 'No version' }) {
    return (
        <div className="intake-stage-context intake-prompt-preview-panel">
            <div className="intake-stage-context__header">
                <h3>{title}</h3>
                {version ? <StatusBadge value={version.status} /> : null}
            </div>
            {text ? (
                <pre className="intake-draft-placeholder" style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>{text}</pre>
            ) : <AdminEmptyState title={emptyTitle} />}
        </div>
    );
}



function PromptDiff({ publishedVersion, selectedVersion, selectedForm }) {
    const leftText = buildPromptTextFromVersion(publishedVersion);
    const rightText = selectedVersion ? buildPromptTextFromForm(selectedForm) : '';
    const rows = buildLineDiff(leftText, rightText);
    const hasChanges = rows.some((row) => row.type !== 'same');
    if (!publishedVersion || !selectedVersion) {
        return <AdminEmptyState title="Select published and draft versions to compare" />;
    }
    return (
        <div className="intake-prompt-diff">
            <div className="intake-prompt-diff__header">
                <strong>Published v{publishedVersion.version_number} to selected v{selectedVersion.version_number}</strong>
                <span className={`intake-badge ${hasChanges ? 'intake-badge--warning' : 'intake-badge--success'}`}>{hasChanges ? 'Changes found' : 'No changes'}</span>
            </div>
            <pre className="intake-prompt-diff__body">
                {rows.map((row, index) => (
                    <span className={`intake-prompt-diff__line intake-prompt-diff__line--${row.type}`} key={`${row.type}-${index}`}>
                        {row.type === 'added' ? '+ ' : row.type === 'removed' ? '- ' : '  '}{row.text || ' '}
                    </span>
                ))}
            </pre>
        </div>
    );
}



function parseStageConfig(version) {
    if (Array.isArray(version?.stages)) return version.stages;
    if (!version?.stage_config_json) return [];
    try {
        const parsed = JSON.parse(version.stage_config_json);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
}



function getPipelineStageOptions(currentPublished) {
    const stages = parseStageConfig(currentPublished);
    const configured = stages
        .filter((stage) => stage?.key)
        .sort((left, right) => Number(left.order || 0) - Number(right.order || 0))
        .map((stage) => [stage.key, stage.label || titleize(stage.key)]);
    return configured.length ? configured : STAGE_OPTIONS;
}



function normalizeStageDraft(stages) {
    return (stages || []).map((stage, index) => {
        const defaultPolicy = DEFAULT_STAGE_POLICY[stage.key] || { stage_type: 'enrichment', can_request_review_gate: false };
        return {
            key: stage.key,
            label: stage.label || titleize(stage.key),
            description: stage.description || STAGE_DESCRIPTIONS[stage.key] || '',
            core: Boolean(stage.core || CORE_STAGE_KEYS.includes(stage.key)),
            enabled: stage.enabled !== false,
            order: index,
            stage_type: stage.stage_type || defaultPolicy.stage_type,
            can_request_review_gate: typeof stage.can_request_review_gate === 'boolean' ? stage.can_request_review_gate : defaultPolicy.can_request_review_gate,
            agents: (stage.agents || []).map((agent, agentIndex) => ({
                agent_id: agent.agent_id || agent.id || null,
                agent_key: agent.agent_key || agent.key,
                display_name: agent.display_name,
                enabled: agent.enabled !== false,
                agent_role: agent.agent_role || agent.role || 'specialist',
                order: agentIndex,
                prompt_version_id: agent.prompt_version_id || null,
                prompt_version_number: agent.prompt_version_number || null,
                model_tier_override: agent.model_tier_override || null,
            })),
        };
    });
}



function getStageRuntimeSteps(agents = []) {
    const enabledAgents = (agents || []).filter((agent) => agent.enabled !== false);
    const finalAgents = enabledAgents.filter((agent) => FINAL_EVAL_AGENT_ROLES.has(agent.agent_role || 'specialist'));
    const queuedAgents = enabledAgents.filter((agent) => !FINAL_EVAL_AGENT_ROLES.has(agent.agent_role || 'specialist'));
    const steps = [];
    if (queuedAgents.length) steps.push({ type: 'parallel', label: 'Queued together', agents: queuedAgents });
    if (finalAgents.length) steps.push({ type: 'single', label: queuedAgents.length ? 'Runs after queued agents complete' : 'Final evaluation', agents: finalAgents });
    return steps;
}



function RuntimeAgentCard({ agent }) {
    const readiness = agent.readiness;
    return (
        <div className="intake-admin-runtime-agent-card">
            <strong>{agent.display_name || titleize(agent.agent_key)}</strong>
            <span>{titleize(agent.agent_role || 'specialist')}{readiness?.runnable === false ? ` / ${readiness.reason || 'Unavailable'}` : ''}</span>
        </div>
    );
}



function StageRuntimeGraph({ agents }) {
    const steps = getStageRuntimeSteps(agents);
    return (
        <div className="intake-admin-runtime-graph" aria-label="Stage runtime graph">
            {steps.length ? steps.map((step, index) => (
                <div className={`intake-admin-runtime-step intake-admin-runtime-step--${step.type}`} key={`${step.type}-${index}`}>
                    <div className="intake-admin-runtime-step__label">
                        <span>Substage {index + 1}</span>
                        <strong>{step.label}</strong>
                    </div>
                    <div className="intake-admin-runtime-step__agents">
                        {step.agents.map((agent) => <RuntimeAgentCard agent={agent} key={agent.agent_key} />)}
                    </div>
                </div>
            )) : <div className="intake-stage-action-empty">No active agents in this stage.</div>}
        </div>
    );
}



function slugifyKey(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

const ATTORNEY_ROUTING_DESCRIPTION_MAX_LENGTH = 10000;



function attorneyRoutingDescription(profile) {
    return profile?.intake_routing_description || '';
}



function AttorneyRoutingProfiles({ profiles = [], loading, onProfilesChanged }) {
    const showToast = useContext(ToastContext);
    const [drafts, setDrafts] = useState({});
    const [savingId, setSavingId] = useState(null);

    useEffect(() => {
        const nextDrafts = {};
        profiles.forEach((profile) => {
            nextDrafts[profile.id] = attorneyRoutingDescription(profile);
        });
        setDrafts(nextDrafts);
    }, [profiles]);

    const updateDraft = useCallback(

    (attorneyId, value) => {
        setDrafts((current) => ({ ...current, [attorneyId]: value }));
    }, []);

    const saveProfile = useCallback(

    async (profile) => {
        const draftValue = drafts[profile.id] || '';
        setSavingId(profile.id);
        try {
            await updateIntakeAttorneyRoutingProfile(profile.id, { intake_routing_description: draftValue });
            showToast({ severity: 'success', summary: 'Saved', detail: `${profile.name || profile.email} routing profile updated` });
            if (typeof onProfilesChanged === 'function') {
                await onProfilesChanged();
            }
        } catch (error) {
            console.error('Failed to update attorney routing profile:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to update attorney routing profile' });
        } finally {
            setSavingId(null);
        }
    }, [drafts, onProfilesChanged, showToast]);

    return (
        <AdminSection
            title="Attorney Routing"
            description="Internal routing guidance for final evaluation. These descriptions are fed to the final eval agent and are not shown to attorneys."
        >
            <AdminCard title="Routing Profiles" description="Active attorneys are listed here; availability controls whether final eval may route a case to them.">
                {loading && !profiles.length ? <AdminEmptyState title="Loading attorney routing profiles..." /> : null}
                {!loading && !profiles.length ? <AdminEmptyState title="No active attorneys found" description="Add active attorneys in People before configuring intake routing." /> : null}
                {profiles.length ? (
                    <div className="intake-attorney-routing-list">
                        {profiles.map(

                        (profile) => {
                            const savedValue = attorneyRoutingDescription(profile);
                            const draftValue = drafts[profile.id] ?? savedValue;
                            const isDirty = draftValue !== savedValue;
                            const isSaving = savingId === profile.id;
                            const isTooLong = draftValue.length > ATTORNEY_ROUTING_DESCRIPTION_MAX_LENGTH;
                            return (
                                <div className="intake-attorney-routing-row" key={profile.id}>
                                    <div className="intake-attorney-routing-row__header">
                                        <div className="intake-attorney-routing-row__identity">
                                            {profile.profile_pic ? <img src={profile.profile_pic} alt="" /> : <div aria-hidden="true">{(profile.name || profile.email || '?').slice(0, 1).toUpperCase()}</div>}
                                            <div>
                                                <strong>{profile.name || profile.email}</strong>
                                                <span>{profile.email || 'No email configured'}</span>
                                            </div>
                                        </div>
                                        <div className="intake-attorney-routing-row__meta">
                                            <span className={`intake-badge ${profile.is_available ? 'intake-badge--success' : 'intake-badge--muted'}`}>
                                                {profile.is_available ? 'Available' : 'Unavailable'}
                                            </span>
                                            {!savedValue.trim() ? <span className="intake-badge intake-badge--warning">Needs routing profile</span> : null}
                                            <UnsavedBadge visible={isDirty} />
                                        </div>
                                    </div>
                                    <label className="intake-attorney-routing-row__field">
                                        <span>Routing profile for {profile.name || profile.email}</span>
                                        <textarea
                                            rows={5}
                                            value={draftValue}
                                            disabled={isSaving}
                                            aria-label={`Routing profile for ${profile.name || profile.email}`}
                                            onChange={(event) => updateDraft(profile.id, event.target.value)}
                                            placeholder="Ideal matters, exclusions, jurisdiction notes, workload constraints, and other routing caveats."
                                        />
                                    </label>
                                    <div className="intake-attorney-routing-row__footer">
                                        <span className={isTooLong ? 'intake-error' : ''}>
                                            {draftValue.length}/{ATTORNEY_ROUTING_DESCRIPTION_MAX_LENGTH}
                                        </span>
                                        {!isDirty && !isSaving ? (
                                            <span className="intake-attorney-routing-row__saved">Saved</span>
                                        ) : (
                                            <button
                                                type="button"
                                                className="intake-attorney-routing-row__save-button"
                                                disabled={isTooLong || Boolean(savingId)}
                                                onClick={() => saveProfile(profile)}
                                            >
                                                <i className={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-save'} aria-hidden="true" />
                                                <span>{isSaving ? 'Saving' : 'Save changes'}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : null}
            </AdminCard>
        </AdminSection>
    );
}



function IntakeAdmin({ embedded = false }) {
    const showToast = useContext(ToastContext);
    const [agents, setAgents] = useState([]);
    const [pipelineVersions, setPipelineVersions] = useState([]);
    const [currentPipelineVersion, setCurrentPipelineVersion] = useState(null);
    const [modelProfiles, setModelProfiles] = useState([]);
    const [tiers, setTiers] = useState(['mini', 'medium', 'flagship', 'fallback']);
    const [roles, setRoles] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [people, setPeople] = useState([]);
    const [requiredRoleKeys, setRequiredRoleKeys] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [operationalSettings, setOperationalSettings] = useState([]);
    const [attorneyRoutingProfiles, setAttorneyRoutingProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const loadAdminData = useCallback(

    async (showRefreshedToast = false) => {
        setLoading(true);
        try {
            const results = await Promise.allSettled([
                fetchIntakeAdminMetrics().then((response) => {
                    setMetrics(response.data || null);
                }),
                fetchIntakeAgents().then((response) => {
                    setAgents(response.data.agents || []);
                }),
                fetchIntakePipelineVersions().then((response) => {
                    setPipelineVersions(response.data.pipeline_versions || []);
                    setCurrentPipelineVersion(response.data.current_published || null);
                }),
                fetchIntakeModelProfiles().then((response) => {
                    setModelProfiles(response.data.model_profiles || []);
                    setTiers(response.data.tiers || ['mini', 'medium', 'flagship', 'fallback']);
                }),
                fetchIntakeStaffRoles().then(

                (response) => {
                    setRoles(response.data.roles || []);
                    setAssignments(response.data.assignments || []);
                    setPeople(response.data.people || []);
                    setRequiredRoleKeys(response.data.required_role_keys || []);
                }),
                fetchIntakeOperationalSettings().then((response) => {
                    setOperationalSettings(response.data.settings || []);
                }),
                fetchIntakeAttorneyRoutingProfiles().then((response) => {
                    setAttorneyRoutingProfiles(response.data.attorneys || []);
                }),
            ]);
            const failed = results.find((result) => result.status === 'rejected');
            if (failed) {
                throw failed.reason;
            }
            if (showRefreshedToast) {
                showToast({ severity: 'info', summary: 'Refreshed', detail: 'Intake admin data refreshed' });
            }
        } catch (error) {
            console.error('Failed to load intake admin data:', error);
            showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load Intake admin data' });
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        loadAdminData(false);
    }, [loadAdminData]);

    const Wrapper = embedded ? AdminSection : AdminPage;

    return (
        <Wrapper
            title="Intake Configuration"
            description="Manage Intake prompts, model tiers, staff routing, and operational health."
            actions={<AppButton icon="pi pi-refresh" variant="secondary" label={loading ? 'Refreshing' : 'Refresh'} onClick={() => loadAdminData(true)} disabled={loading} />}
        >
            <AppTabView activeIndex={activeIndex} onTabChange={(event) => setActiveIndex(event.index)} variant={embedded ? 'default' : 'framed'}>
                <AppTab header="Agent Prompts">
                    <PromptStudio
                        agents={agents}
                        stageOptions={getPipelineStageOptions(currentPipelineVersion)}
                        onAgentsChanged={() => loadAdminData(false)}
                    />
                </AppTab>
                <AppTab header="Firm Context">
                    <GlobalPromptStudio onPromptsChanged={() => loadAdminData(false)} />
                </AppTab>
                <AppTab header="Class Actions">
                    <GlobalPromptStudio
                        promptKey={CURRENT_CLASS_ACTIONS_PROMPT_KEY}
                        versionsTitle="Class Action Reference Versions"
                        versionsDescription="This reference is appended to triage routing prompts so Intake can spot current class-action overlap."
                        emptyVersionsTitle="No class action reference versions"
                        editorTitle="Class Actions Reference Editor"
                        editorEmptyDescription="Select or create a class action reference version."
                        promptLabel="Current Class Actions Reference"
                        previewCardTitle="Class Actions Reference Preview"
                        previewTitle="Published Class Actions Reference"
                        createDraftNotes="Draft created from current published class actions reference"
                        createDraftSuccessDetail="Class actions reference draft created"
                        restoreDraftNotes={(version) => `Restored from class actions reference version ${version.version_number}`}
                        restoreDraftSuccessDetail="Class actions reference restored as a new draft"
                        saveSuccessDetail="Class actions reference draft saved"
                        publishSuccessDetail="Class actions reference published"
                        archiveSuccessDetail="Class actions reference archived"
                        onPromptsChanged={() => loadAdminData(false)}
                    />
                </AppTab>
                <AppTab header="Pipeline Versions">
                    <PipelineVersions
                        agents={agents}
                        versions={pipelineVersions}
                        currentPublished={currentPipelineVersion}
                        onPipelineChanged={() => loadAdminData(false)}
                    />
                </AppTab>
                <AppTab header="Model Catalog">
                    <ModelCatalog profiles={modelProfiles} tiers={tiers} onProfilesChanged={() => loadAdminData(false)} />
                </AppTab>
                <AppTab header="Staff Roles">
                    <StaffRoles
                        roles={roles}
                        assignments={assignments}
                        people={people}
                        requiredRoleKeys={requiredRoleKeys}
                        onRolesChanged={() => loadAdminData(false)}
                    />
                </AppTab>
                <AppTab header="Attorney Routing">
                    <AttorneyRoutingProfiles
                        profiles={attorneyRoutingProfiles}
                        loading={loading}
                        onProfilesChanged={() => loadAdminData(false)}
                    />
                </AppTab>
                <AppTab header="Operations">
                    <OperationalSettings
                        settings={operationalSettings}
                        delivery={metrics?.outreach_delivery}
                        onSettingsChanged={() => loadAdminData(false)}
                    />
                </AppTab>
                <AppTab header="Health">
                    <IntakeHealth metrics={metrics} loading={loading} />
                </AppTab>
            </AppTabView>
        </Wrapper>
    );
}



function IntakeHealth({ metrics, loading }) {
    const statusRows = metrics?.lead_counts?.by_status || [];
    const stageRows = metrics?.lead_counts?.by_stage || [];
    const roleRows = metrics?.role_coverage || [];
    const unrunnableAgents = metrics?.agent_readiness?.unrunnable_agents || [];
    const emptyStages = metrics?.pipeline_stage_coverage?.empty_active_stages || [];
    const placeholderProfiles = metrics?.model_coverage?.placeholder_profiles || [];
    const outreachDelivery = metrics?.outreach_delivery || {};
    const outreachPolicy = outreachDelivery.policy || {};
    const outreachNotice = getOutreachDeliveryNotice(outreachPolicy, outreachDelivery);

    if (loading && !metrics) return <AdminEmptyState title="Loading Intake health..." />;
    if (!metrics) return <AdminEmptyState title="No Intake health data loaded" />;

    return (
        <AdminSection>
            <div className="intake-decision-grid">
                <StatTile label="Total Leads" value={metrics.lead_counts?.total || 0} />
                <StatTile label="Published Prompts" value={`${metrics.prompt_coverage?.agents_with_published_prompt || 0}/${metrics.prompt_coverage?.agents || 0}`} />
                <StatTile label="Pipeline Version" value={metrics.pipeline_coverage?.published_version ? `v${metrics.pipeline_coverage.published_version}` : '-'} detail={`${metrics.pipeline_coverage?.unpinned_leads || 0} unpinned leads`} />
                <StatTile label="Model Tiers Missing" value={(metrics.model_coverage?.missing_tiers || []).length} detail={(metrics.model_coverage?.missing_tiers || []).join(', ') || 'All tiers active'} />
                <StatTile label="Runnable Agents" value={`${metrics.agent_readiness?.runnable_agents || 0}/${metrics.agent_readiness?.agents || 0}`} detail={unrunnableAgents.length ? `${unrunnableAgents.length} blocked` : 'All runnable'} />
                <StatTile label="Placeholder Models" value={placeholderProfiles.length} detail={(metrics.model_coverage?.placeholder_tiers || []).join(', ') || 'None'} />
                <StatTile label="Open Staff Work" value={(metrics.open_work?.staff_reviews || 0) + (metrics.open_work?.open_outreach || 0) + (metrics.open_work?.pending_documents || 0)} detail="reviews, outreach, documents" />
                <StatTile label="Outreach Delivery" value={getOutreachDeliveryModeLabel(outreachPolicy)} detail={getOutreachDeliveryDetail(outreachPolicy, outreachDelivery)} tone={getOutreachDeliveryTone(outreachPolicy, outreachDelivery)} />
            </div>

            {outreachNotice ? (
                <div className={`intake-admin-callout intake-admin-callout--${outreachNotice.tone}`}>
                    <strong>{outreachNotice.title}</strong>
                    <span>{outreachNotice.detail}</span>
                </div>
            ) : null}

            <div className="intake-split-panel">
                <AdminCard title="Lead Status">
                    <SimpleMetricList rows={statusRows} />
                </AdminCard>
                <AdminCard title="Lead Stage">
                    <SimpleMetricList rows={stageRows} />
                </AdminCard>
            </div>

            <ReviewGateMetrics metrics={metrics.review_gate_metrics} />

            <AdminCard title="Required Role Coverage">
                <div className="intake-record-list">
                    {roleRows.map((row) => (
                        <div className="intake-record-row" key={row.role_key}>
                            <div>
                                <strong>{titleize(row.role_key)}</strong>
                                <span>{row.active_assignments} active assignment{row.active_assignments === 1 ? '' : 's'}</span>
                            </div>
                            <StatusBadge value={row.covered ? 'active' : 'inactive'} />
                        </div>
                    ))}
                </div>
            </AdminCard>
            <div className="intake-split-panel">
                <AdminCard title="Unrunnable Agents">
                    <SimpleReadinessList rows={unrunnableAgents} emptyTitle="All agents runnable" />
                </AdminCard>
                <AdminCard title="Empty Active Stages">
                    <SimpleReadinessList rows={emptyStages} emptyTitle="No empty active stages" />
                </AdminCard>
            </div>
        </AdminSection>
    );
}



function ReviewGateMetrics({ metrics }) {
    const totals = metrics?.totals || {};
    const perAgent = metrics?.per_agent || [];
    const actualGateCount = (totals.staff_review_gate_runs || 0) + (totals.attorney_review_gate_runs || 0);
    const topAgents = perAgent
        .filter((row) => (row.advisory_risk_flag_runs || 0) || (row.legacy_human_flag_runs || 0) || (row.staff_review_gate_runs || 0) || (row.attorney_review_gate_runs || 0))
        .slice(0, 6);

    return (
        <AdminCard title={`Review Gates${metrics?.window_days ? ` (${metrics.window_days}d)` : ''}`}>
            <div className="intake-record-list">
                <div className="intake-record-row">
                    <div>
                        <strong>Advisory Risk Flags</strong>
                        <span>{totals.completed_runs || 0} completed agent run{totals.completed_runs === 1 ? '' : 's'}</span>
                    </div>
                    <span className="intake-badge intake-badge--info">{totals.advisory_risk_flag_runs || 0}</span>
                </div>
                <div className="intake-record-row">
                    <div>
                        <strong>Legacy Human Flags</strong>
                        <span>Compatibility-only flag fields</span>
                    </div>
                    <span className="intake-badge intake-badge--warning">{totals.legacy_human_flag_runs || 0}</span>
                </div>
                <div className="intake-record-row">
                    <div>
                        <strong>Actual Review Gates</strong>
                        <span>{totals.staff_review_gate_runs || 0} staff, {totals.attorney_review_gate_runs || 0} attorney</span>
                    </div>
                    <span className="intake-badge intake-badge--info">{actualGateCount}</span>
                </div>
                {topAgents.map((row) => (
                    <div className="intake-record-row" key={row.agent_key}>
                        <div>
                            <strong>{row.agent_display_name || titleize(row.agent_key)}</strong>
                            <span>{row.advisory_risk_flag_runs || 0} risk, {row.legacy_human_flag_runs || 0} legacy, {(row.staff_review_gate_runs || 0) + (row.attorney_review_gate_runs || 0)} gate</span>
                        </div>
                        <span className="intake-badge intake-badge--info">{row.completed_runs || 0}</span>
                    </div>
                ))}
            </div>
        </AdminCard>
    );
}



function OperationalSettings({ settings, delivery, onSettingsChanged }) {
    const showToast = useContext(ToastContext);
    const [form, setForm] = useState(() => settingsArrayToValues(settings));
    const [saving, setSaving] = useState(false);
    const values = settingsArrayToValues(settings);
    const outreachPolicy = delivery?.policy || {};

    useEffect(() => {
        setForm(settingsArrayToValues(settings));
    }, [settings]);

    const updateField =

    (key, value) => {
        setForm((current) => ({ ...current, [key]: value }));
    };

    const saveSettings =

    async () => {
        setSaving(true);
        try {
            await updateIntakeOperationalSettings({ settings: submissionSettingsFromOperationalForm(form) });
            showToast({ severity: 'success', summary: 'Saved', detail: 'Intake operations settings updated' });
            onSettingsChanged();
        } catch (error) {
            console.error('Failed to save intake operational settings:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to save Intake operations settings' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminSection>
            <div className="intake-decision-grid">
                <StatTile label="Current Delivery" value={getOutreachDeliveryModeLabel(outreachPolicy)} detail={getOutreachDeliveryDetail(outreachPolicy, delivery || {})} tone={getOutreachDeliveryTone(outreachPolicy, delivery || {})} />
                <StatTile label="Upload Mode" value={titleize(values.intake_upload_link_mode)} detail={`Source: ${getSettingSource(settings, 'intake_upload_link_mode')}`} />
                <StatTile label="Dev Simulator" value={values.intake_dev_upload_simulator_enabled ? 'Enabled' : 'Disabled'} detail={`Source: ${getSettingSource(settings, 'intake_dev_upload_simulator_enabled')}`} tone={values.intake_dev_upload_simulator_enabled ? 'warning' : 'info'} />
            </div>

            <AdminCard title="Email Safety">
                <div className="intake-form-grid">
                    <SelectInput label="Delivery Mode" value={form.intake_outreach_delivery_mode} options={OUTREACH_DELIVERY_OPTIONS} onChange={(value) => updateField('intake_outreach_delivery_mode', value)} />
                    <TextInput label="Redirect/Test Recipient" value={form.intake_outreach_redirect_email} onChange={(value) => updateField('intake_outreach_redirect_email', value)} />
                    <TextInput label="Sender Mailbox" value={delivery?.sender_email || 'intake@kneuppercovey.com'} disabled onChange={() => {}} />
                </div>
            </AdminCard>

            <AdminCard title="Document Upload Links">
                <div className="intake-form-grid">
                    <SelectInput label="Upload Link Mode" value={form.intake_upload_link_mode} options={UPLOAD_LINK_MODE_OPTIONS} onChange={(value) => updateField('intake_upload_link_mode', value)} />
                    <CheckboxInput label="Enable dev upload simulator" checked={form.intake_dev_upload_simulator_enabled} onChange={(value) => updateField('intake_dev_upload_simulator_enabled', value)} />
                    <TextInput label="External Uploader Base URL" value={form.file_uploader_base_url} onChange={(value) => updateField('file_uploader_base_url', value)} wide />
                    <TextInput label="Dev Upload Base URL" value={form.intake_dev_upload_base_url} onChange={(value) => updateField('intake_dev_upload_base_url', value)} wide />
                </div>
            </AdminCard>

            <AdminCard title="Follow-Up Policy">
                <div className="intake-form-grid">
                    <CheckboxInput label="Enable information request follow-ups" checked={form.information_request_followups_enabled} onChange={(value) => updateField('information_request_followups_enabled', value)} />
                    <TextInput label="Follow-up Interval Days" type="number" value={form.information_request_followup_interval_days} onChange={(value) => updateField('information_request_followup_interval_days', value)} />
                    <TextInput label="Great Case Max Follow-Ups" type="number" value={form.information_request_great_case_max_followups} onChange={(value) => updateField('information_request_great_case_max_followups', value)} />
                    <TextInput label="Ok Case Max Follow-Ups" type="number" value={form.information_request_ok_case_max_followups} onChange={(value) => updateField('information_request_ok_case_max_followups', value)} />
                </div>
            </AdminCard>

            <AdminCard title="Batch Promotion">
                <div className="intake-form-grid">
                    <TextInput label="Batch Window Days" type="number" value={form.intake_batch_window_days} onChange={(value) => updateField('intake_batch_window_days', value)} />
                    <TextInput label="Batch Max Leads" type="number" value={form.intake_batch_max_leads} onChange={(value) => updateField('intake_batch_max_leads', value)} />
                    <TextInput label="Batch Outreach Limit" type="number" value={form.intake_batch_outreach_limit} onChange={(value) => updateField('intake_batch_outreach_limit', value)} />
                    <TextInput label="Information Request Score Threshold" type="number" value={form.information_request_minimum_quality_score} onChange={(value) => updateField('information_request_minimum_quality_score', value)} />
                    <TextInput label="Stale Archive Days" type="number" value={form.intake_stale_archive_days} onChange={(value) => updateField('intake_stale_archive_days', value)} />
                    <TextInput label="Email Acquisition Role" value={form.intake_email_acquisition_role_key} onChange={(value) => updateField('intake_email_acquisition_role_key', value)} />
                </div>
            </AdminCard>

            <div className="intake-action-row">
                <AppButton icon="pi pi-save" label={saving ? 'Saving' : 'Save Operations Settings'} disabled={saving} onClick={saveSettings} />
            </div>
        </AdminSection>
    );
}



function SimpleReadinessList({ rows, emptyTitle }) {
    if (!rows.length) return <AdminEmptyState title={emptyTitle} />;
    return (
        <div className="intake-record-list">
            {rows.map((row) => (
                <div className="intake-record-row" key={row.agent_key || row.stage_key}>
                    <div>
                        <strong>{row.agent_display_name || row.stage_label || titleize(row.agent_key || row.stage_key)}</strong>
                        <span>{row.reason || row.stage_key || row.agent_key}</span>
                    </div>
                    <StatusBadge value="inactive" />
                </div>
            ))}
        </div>
    );
}



function SimpleMetricList({ rows }) {
    if (!rows.length) return <AdminEmptyState title="No rows" />;
    return (
        <div className="intake-record-list">
            {rows.map((row) => (
                <div className="intake-record-row" key={row.value}>
                    <div>
                        <strong>{titleize(row.value)}</strong>
                    </div>
                    <span className="intake-badge intake-badge--info">{row.count}</span>
                </div>
            ))}
        </div>
    );
}



function GlobalPromptStudio({
    promptKey = FIRM_CONTEXT_PROMPT_KEY,
    versionsTitle = 'Firm Context Versions',
    versionsDescription = 'This prompt is prepended to every intake agent system prompt.',
    emptyVersionsTitle = 'No global prompt versions',
    editorTitle = 'Firm Context Editor',
    editorEmptyDescription = 'Select or create a global prompt version.',
    promptLabel = 'Firm Context Prompt',
    previewCardTitle = 'Global Prompt Preview',
    previewTitle = 'Published Firm Context',
    createDraftNotes = 'Draft created from current published firm context',
    createDraftSuccessDetail = 'Global prompt draft created',
    restoreDraftNotes = (version) => `Restored from global prompt version ${version.version_number}`,
    restoreDraftSuccessDetail = 'Global prompt version restored as a new draft',
    saveSuccessDetail = 'Global prompt draft saved',
    publishSuccessDetail = 'Global prompt version published',
    archiveSuccessDetail = 'Global prompt version archived',
    onPromptsChanged = () => {},
}) {
    const showToast = useContext(ToastContext);
    const [versions, setVersions] = useState([]);
    const [selectedVersionId, setSelectedVersionId] = useState('');
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ prompt_text: '', change_notes: '' });

    const selectedVersion = versions.find((version) => String(version.id) === String(selectedVersionId));
    const publishedVersion = versions.find((version) => version.status === 'published');
    const canEdit = selectedVersion?.status === 'draft';
    const canArchive = selectedVersion && selectedVersion.status !== 'archived' && selectedVersion.status !== 'published';

    const loadVersions = useCallback(

    async () => {
        setLoadingVersions(true);
        try {
            const response = await fetchIntakeGlobalPromptVersions(promptKey);
            const nextVersions = response.data.prompt_versions || [];
            setVersions(nextVersions);
            setSelectedVersionId(nextVersions[0] ? String(nextVersions[0].id) : '');
        } catch (error) {
            console.error('Failed to load intake global prompt versions:', error);
            showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load global prompt versions' });
        } finally {
            setLoadingVersions(false);
        }
    }, [promptKey, showToast]);

    useEffect(() => {
        loadVersions();
    }, [loadVersions]);

    useEffect(() => {
        if (!selectedVersion) {
            setForm({ prompt_text: '', change_notes: '' });
            return;
        }
        setForm({
            prompt_text: selectedVersion.prompt_text || '',
            change_notes: selectedVersion.change_notes || '',
        });
    }, [selectedVersion]);

    const createDraftFrom =

    async (sourceVersion, changeNotes, successDetail) => {
        setSaving(true);
        try {
            const response = await createIntakeGlobalPromptVersion(promptKey, {
                source_version_id: sourceVersion?.id,
                change_notes: changeNotes,
            });
            await loadVersions();
            setSelectedVersionId(String(response.data.prompt_version.id));
            onPromptsChanged();
            showToast({ severity: 'success', summary: 'Created', detail: successDetail });
        } catch (error) {
            console.error('Failed to create intake global prompt draft:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to create global prompt draft' });
        } finally {
            setSaving(false);
        }
    };

    const createDraft =

    async () => {
        await createDraftFrom(
            publishedVersion || versions[0],
            createDraftNotes,
            createDraftSuccessDetail
        );
    };

    const restoreAsDraft =

    async () => {
        if (!selectedVersion) return;
        await createDraftFrom(
            selectedVersion,
            restoreDraftNotes(selectedVersion),
            restoreDraftSuccessDetail
        );
    };

    const savePrompt =

    async () => {
        if (!selectedVersion || !canEdit) return;
        setSaving(true);
        try {
            await updateIntakeGlobalPromptVersion(selectedVersion.id, form);
            await loadVersions();
            showToast({ severity: 'success', summary: 'Saved', detail: saveSuccessDetail });
        } catch (error) {
            console.error('Failed to save intake global prompt:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to save global prompt draft' });
        } finally {
            setSaving(false);
        }
    };

    const publishPrompt =

    async () => {
        if (!selectedVersion) return;
        setSaving(true);
        try {
            if (canEdit) {
                await updateIntakeGlobalPromptVersion(selectedVersion.id, form);
            }
            await publishIntakeGlobalPromptVersion(selectedVersion.id, { change_notes: form.change_notes });
            await loadVersions();
            onPromptsChanged();
            showToast({ severity: 'success', summary: 'Published', detail: publishSuccessDetail });
        } catch (error) {
            console.error('Failed to publish intake global prompt:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to publish global prompt' });
        } finally {
            setSaving(false);
        }
    };

    const archivePrompt =

    async () => {
        if (!selectedVersion) return;
        setSaving(true);
        try {
            await archiveIntakeGlobalPromptVersion(selectedVersion.id);
            await loadVersions();
            onPromptsChanged();
            showToast({ severity: 'success', summary: 'Archived', detail: archiveSuccessDetail });
        } catch (error) {
            console.error('Failed to archive intake global prompt:', error);
            showToast({ severity: 'error', summary: 'Error', detail: 'Failed to archive global prompt' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminSection>
            <div className="intake-split-panel">
                <AdminCard title={versionsTitle} description={versionsDescription} actions={(
                    <>
                        <AppButton icon="pi pi-plus" variant="secondary" label="New Draft" onClick={createDraft} disabled={saving} />
                        <AppButton icon="pi pi-refresh" variant="secondary" label="Restore as Draft" onClick={restoreAsDraft} disabled={saving || !selectedVersion} />
                    </>
                )}>
                    {loadingVersions ? <AdminEmptyState title="Loading versions..." /> : (
                        <div className="intake-record-list">
                            {versions.map((version) => (
                                <button
                                    type="button"
                                    className={`intake-draft-card${String(version.id) === String(selectedVersionId) ? ' intake-draft-card--selected' : ''}`}
                                    key={version.id}
                                    onClick={() => setSelectedVersionId(String(version.id))}
                                >
                                    <div className="intake-draft-card__main">
                                        <strong>Version {version.version_number}</strong>
                                        <span>{version.change_notes || 'No notes'}</span>
                                        <small>{version.published_at ? `Published ${formatDate(version.published_at)}` : formatDate(version.updated_at || version.created_at)}</small>
                                    </div>
                                    <StatusBadge value={version.status} />
                                </button>
                            ))}
                            {!versions.length && <AdminEmptyState title={emptyVersionsTitle} />}
                        </div>
                    )}
                </AdminCard>

                <AdminCard
                    title={editorTitle}
                    description={selectedVersion ? `Version ${selectedVersion.version_number} is ${selectedVersion.status}. Published versions are immutable.` : editorEmptyDescription}
                    actions={(
                        <>
                            <AppButton icon="pi pi-save" variant="secondary" label="Save Draft" onClick={savePrompt} disabled={saving || !canEdit} />
                            <AppButton icon="pi pi-check" variant="success" label="Publish" onClick={publishPrompt} disabled={saving || !canEdit} />
                            <AppButton icon="pi pi-box" variant="secondary" label="Archive" onClick={archivePrompt} disabled={saving || !canArchive} />
                        </>
                    )}
                >
                    <div className="intake-form-grid">
                        <TextInput label="Change Notes" value={form.change_notes} disabled={!canEdit} onChange={(value) => setForm((current) => ({ ...current, change_notes: value }))} wide />
                        <TextArea label={promptLabel} value={form.prompt_text} rows={18} disabled={!canEdit} onChange={(value) => setForm((current) => ({ ...current, prompt_text: value }))} />
                    </div>
                </AdminCard>
            </div>

            <AdminCard title={previewCardTitle}>
                <PromptPreview title={previewTitle} version={publishedVersion} promptTextField="prompt_text" />
            </AdminCard>
        </AdminSection>
    );
}



function PromptStudio({ agents, stageOptions, onAgentsChanged }) {
    const showToast = useContext(ToastContext);
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const [versions, setVersions] = useState([]);
    const [selectedVersionId, setSelectedVersionId] = useState('');
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [globalPromptVersions, setGlobalPromptVersions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
    const [newAgentForm, setNewAgentForm] = useState(EMPTY_AGENT_FORM);
    const [agentForm, setAgentForm] = useState({ display_name: '', description: '', stage_key: 'triage', default_model_tier: 'mini', active: true });
    const [pendingPromptFocus, setPendingPromptFocus] = useState('');
    const systemPromptRef = useRef(null);
    const userPromptRef = useRef(null);
    const creatingPromptDraftRef = useRef(false);
    const [promptForm, setPromptForm] = useState({
        system_prompt: '',
        user_prompt_template: '',
        variables_schema_json: '',
        output_schema_json: '',
        model_tier: 'mini',
        model_settings: { ...EMPTY_PROMPT_MODEL_SETTINGS },
        change_notes: '',
    });

    const selectedAgent = agents.find((agent) => String(agent.id) === String(selectedAgentId));
    const selectedVersion = versions.find((version) => String(version.id) === String(selectedVersionId));
    const publishedVersion = versions.find((version) => version.status === 'published');
    const publishedFirmContext = globalPromptVersions.find((version) => version.status === 'published');
    const canEditPrompt = selectedVersion?.status === 'draft';
    const canArchivePrompt = selectedVersion && selectedVersion.status !== 'archived' && selectedVersion.status !== 'published';
    const agentDirty = Boolean(selectedAgent && JSON.stringify(agentForm) !== JSON.stringify(getAgentFormFromAgent(selectedAgent)));
    const promptDirty = Boolean(selectedVersion && JSON.stringify(promptForm) !== JSON.stringify(getPromptFormFromVersion(selectedVersion, selectedAgent)));
    const templateAnalysis = useMemo(() => getTemplateVariableAnalysis(promptForm.user_prompt_template, promptForm.variables_schema_json), [promptForm.user_prompt_template, promptForm.variables_schema_json]);
    const variableSchemaMessages = templateAnalysis.missing.map((variable) => `Template variable ${variable} is missing from the variables schema.`);
    const promptLockMessage = !selectedAgent
        ? 'Select or create an agent before editing prompts.'
        : !selectedVersion
            ? 'Select a prompt version or create a draft.'
            : canEditPrompt
                ? `Draft version ${selectedVersion.version_number} can be edited. Save it before publishing.`
                : selectedVersion.status === 'published'
                    ? 'Published prompt versions are read-only. Create a draft from the published prompt before editing.'
                    : 'Archived prompt versions are read-only. Create a draft from the selected version before editing.';
    const createDraftLabel = publishedVersion ? 'New Draft from Published' : 'New Blank Draft';
    const createDraftTitle = publishedVersion
        ? `Create a draft from published version ${publishedVersion.version_number}`
        : 'Create a blank draft for this agent';
    const promptJsonErrors = getPromptJsonErrors(promptForm);
    const promptHealthLabel = promptJsonErrors.length
        ? `${promptJsonErrors.length} schema issue${promptJsonErrors.length === 1 ? '' : 's'}`
        : templateAnalysis.missing.length
            ? `${templateAnalysis.missing.length} missing variable${templateAnalysis.missing.length === 1 ? '' : 's'}`
            : 'Ready';
    const versionOptions = versions.map((version) => [
        String(version.id),
        `Version ${version.version_number} (${titleize(version.status)})`,
    ]);

    useEffect(() => {
        if (!selectedAgentId && agents.length) {
            setSelectedAgentId(String(agents[0].id));
        }
    }, [agents, selectedAgentId]);

    useEffect(() => {
        if (!selectedAgent) return;
        setAgentForm(getAgentFormFromAgent(selectedAgent));
    }, [selectedAgent]);

    const loadGlobalPromptVersions = useCallback(

    async () => {
        try {
            const response = await fetchIntakeGlobalPromptVersions(FIRM_CONTEXT_PROMPT_KEY);
            setGlobalPromptVersions(response.data.prompt_versions || []);
        } catch (error) {
            console.error('Failed to load intake global prompt versions for preview:', error);
            showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load firm context preview' });
        }
    }, [showToast]);

    useEffect(() => {
        loadGlobalPromptVersions();
    }, [loadGlobalPromptVersions]);

    const loadVersions = useCallback(

    async () => {
        if (!selectedAgentId) {
            setVersions([]);
            setSelectedVersionId('');
            return;
        }
        setLoadingVersions(true);
        try {
            const response = await fetchIntakePromptVersions(selectedAgentId);
            const nextVersions = response.data.prompt_versions || [];
            const preferredVersion = nextVersions.find((version) => version.status === 'draft')
                || nextVersions.find((version) => version.status === 'published')
                || nextVersions[0];
            setVersions(nextVersions);
            setSelectedVersionId(preferredVersion ? String(preferredVersion.id) : '');
        } catch (error) {
            console.error('Failed to load intake prompt versions:', error);
            showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load prompt versions' });
        } finally {
            setLoadingVersions(false);
        }
    }, [selectedAgentId, showToast]);

    useEffect(() => {
        loadVersions();
    }, [loadVersions]);

    useEffect(() => {
        setPromptForm(getPromptFormFromVersion(selectedVersion, selectedAgent));
    }, [selectedAgent, selectedVersion]);

    useEffect(

    () => {
        if (!pendingPromptFocus || !canEditPrompt) return undefined;
        const promptRef = pendingPromptFocus === 'system_prompt' ? systemPromptRef : userPromptRef;
        const focusFrame = window.requestAnimationFrame(

        () => {
            promptRef.current?.focus();
            const promptLength = promptRef.current?.value?.length || 0;
            promptRef.current?.setSelectionRange?.(promptLength, promptLength);
            setPendingPromptFocus('');
        });
        return () => window.cancelAnimationFrame(focusFrame);
    }, [canEditPrompt, pendingPromptFocus, selectedVersionId]);

    const saveAgent =

    async () => {
        if (!selectedAgent) return;
        setSaving(true);
        try {
            await updateIntakeAgent(selectedAgent.id, agentForm);
            showToast({ severity: 'success', summary: 'Saved', detail: 'Agent settings updated' });
            onAgentsChanged();
        } catch (error) {
            console.error('Failed to save intake agent:', error);
            showToast({ severity: 'error', summary: 'Error', detail: 'Failed to save agent settings' });
        } finally {
            setSaving(false);
        }
    };

    const createDraftFrom =

    async (sourceVersion, changeNotes, successDetail) => {
        if (!selectedAgent) return;
        setSaving(true);
        try {
            const response = await createIntakePromptVersion(selectedAgent.id, {
                source_version_id: sourceVersion?.id,
                change_notes: changeNotes,
            });
            await loadVersions();
            setSelectedVersionId(String(response.data.prompt_version.id));
            showToast({ severity: 'success', summary: 'Created', detail: successDetail });
            onAgentsChanged();
            return response.data.prompt_version;
        } catch (error) {
            console.error('Failed to create intake prompt draft:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to create prompt draft' });
            return null;
        } finally {
            setSaving(false);
        }
    };

    const createDraft =

    async () => {
        const sourceVersion = publishedVersion || versions[0];
        await createDraftFrom(
            sourceVersion,
            sourceVersion ? `Draft created from prompt version ${sourceVersion.version_number}` : 'Blank prompt draft created',
            sourceVersion ? 'Draft created from selected source prompt' : 'Blank prompt draft created'
        );
    };

    const createDraftFromSelected =

    async () => {
        if (!selectedVersion) return;
        await createDraftFrom(
            selectedVersion,
            `Draft cloned from prompt version ${selectedVersion.version_number}`,
            'Draft created from selected prompt version'
        );
    };

    const createDraftFromPromptField =

    async (field) => {
        if (canEditPrompt || saving || creatingPromptDraftRef.current || !selectedAgent) return;
        creatingPromptDraftRef.current = true;
        setPendingPromptFocus(field);
        const sourceVersion = selectedVersion || publishedVersion || versions[0];
        const createdDraft = await createDraftFrom(
            sourceVersion,
            sourceVersion ? `Draft cloned from prompt version ${sourceVersion.version_number}` : 'Blank prompt draft created',
            sourceVersion ? 'Draft created from selected prompt version' : 'Blank prompt draft created'
        );
        if (!createdDraft) {
            setPendingPromptFocus('');
        }
        creatingPromptDraftRef.current = false;
    };

    const savePrompt =

    async () => {
        if (!selectedVersion || !canEditPrompt) return;
        const jsonErrors = getPromptJsonErrors(promptForm);
        if (jsonErrors.length) {
            showToast({ severity: 'warn', summary: 'Invalid Schema', detail: jsonErrors.join(' ') });
            return;
        }
        setSaving(true);
        try {
            await updateIntakePromptVersion(selectedVersion.id, buildPromptPayload(promptForm));
            await loadVersions();
            showToast({ severity: 'success', summary: 'Saved', detail: 'Prompt draft saved' });
        } catch (error) {
            console.error('Failed to save intake prompt:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to save prompt draft' });
        } finally {
            setSaving(false);
        }
    };

    const updatePromptModelSetting =

    (field, value) => {
        setPromptForm((current) => ({
            ...current,
            model_settings: {
                ...current.model_settings,
                [field]: value,
            },
        }));
    };

    const publishPrompt =

    async () => {
        if (!selectedVersion) return;
        const jsonErrors = getPromptJsonErrors(promptForm);
        if (jsonErrors.length) {
            showToast({ severity: 'warn', summary: 'Invalid Schema', detail: jsonErrors.join(' ') });
            return;
        }
        setSaving(true);
        try {
            if (canEditPrompt) {
                await updateIntakePromptVersion(selectedVersion.id, buildPromptPayload(promptForm));
            }
            await publishIntakePromptVersion(selectedVersion.id, { change_notes: promptForm.change_notes });
            await loadVersions();
            onAgentsChanged();
            showToast({ severity: 'success', summary: 'Published', detail: 'Prompt version published' });
        } catch (error) {
            console.error('Failed to publish intake prompt:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to publish prompt' });
        } finally {
            setSaving(false);
        }
    };

    const archivePrompt =

    async () => {
        if (!selectedVersion) return;
        setSaving(true);
        try {
            await archiveIntakePromptVersion(selectedVersion.id);
            await loadVersions();
            onAgentsChanged();
            showToast({ severity: 'success', summary: 'Archived', detail: 'Prompt version archived' });
        } catch (error) {
            console.error('Failed to archive intake prompt:', error);
            showToast({ severity: 'error', summary: 'Error', detail: 'Failed to archive prompt' });
        } finally {
            setSaving(false);
        }
    };

    const createAgent =

    async (event) => {
        event?.preventDefault();
        if (!newAgentForm.key || !newAgentForm.display_name) return;
        setSaving(true);
        try {
            const response = await createIntakeAgent({
                ...newAgentForm,
                key: slugifyKey(newAgentForm.key),
            });
            setNewAgentForm(EMPTY_AGENT_FORM);
            setShowCreateAgentModal(false);
            if (response.data.agent?.id) {
                setSelectedAgentId(String(response.data.agent.id));
            }
            onAgentsChanged();
            showToast({ severity: 'success', summary: 'Created', detail: 'Specialist agent created' });
        } catch (error) {
            console.error('Failed to create intake agent:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to create specialist agent' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminSection
            title="Agent Prompts"
            description="Pick an agent, edit its prompt draft, then save or publish it."
            actions={<AppButton icon="pi pi-plus" label="New Agent" onClick={() => setShowCreateAgentModal(true)} />}
        >
            <AppModal
                visible={showCreateAgentModal}
                title="Create Specialist Agent"
                description="Add the agent here, then assign it to stages in a draft pipeline version."
                onClose={() => setShowCreateAgentModal(false)}
                width="760px"
                footer={(
                    <>
                        <AppButton variant="secondary" label="Cancel" onClick={() => setShowCreateAgentModal(false)} disabled={saving} />
                        <AppButton icon="pi pi-plus" label="Create Agent" onClick={createAgent} disabled={saving || !newAgentForm.key || !newAgentForm.display_name} />
                    </>
                )}
            >
                <form className="intake-edit-form" onSubmit={createAgent}>
                    <div className="intake-form-grid">
                        <TextInput label="Agent Key" value={newAgentForm.key} onChange={(value) => setNewAgentForm((current) => ({ ...current, key: slugifyKey(value) }))} />
                        <TextInput label="Display Name" value={newAgentForm.display_name} onChange={(value) => setNewAgentForm((current) => ({ ...current, display_name: value }))} />
                        <SelectInput label="Default Stage" value={newAgentForm.stage_key} onChange={(value) => setNewAgentForm((current) => ({ ...current, stage_key: value }))} options={stageOptions} />
                        <SelectInput label="Default Tier" value={newAgentForm.default_model_tier} onChange={(value) => setNewAgentForm((current) => ({ ...current, default_model_tier: value }))} options={MODEL_TIER_OPTIONS} />
                        <CheckboxInput label="Active" checked={newAgentForm.active} onChange={(value) => setNewAgentForm((current) => ({ ...current, active: value }))} />
                        <TextArea label="Description" value={newAgentForm.description} rows={3} onChange={(value) => setNewAgentForm((current) => ({ ...current, description: value }))} />
                    </div>
                </form>
            </AppModal>

            {!agents.length ? <AdminEmptyState title="No agents configured" /> : null}

            {agents.length ? (
                <div className="intake-prompt-workspace">
                    <aside className="intake-prompt-selector-sidebar" aria-label="Prompt selection">
                        <div className="intake-prompt-selector-panel">
                            <div className="intake-prompt-selector-panel__header">
                                <strong>Select Prompt</strong>
                                <span>Choose the agent and prompt version to edit.</span>
                            </div>
                            <div className="intake-prompt-selector-fields">
                                <div className="intake-prompt-agent-picker">
                                    <span className="intake-prompt-agent-picker__label">Agent</span>
                                    <div className="intake-prompt-agent-list" role="listbox" aria-label="Agent">
                                        {agents.map((agent) => {
                                            const selected = String(agent.id) === String(selectedAgentId);
                                            return (
                                                <button
                                                    type="button"
                                                    role="option"
                                                    aria-selected={selected}
                                                    aria-label={`${agent.display_name} (${agent.key})`}
                                                    className={`intake-prompt-agent-item${selected ? ' intake-prompt-agent-item--active' : ''}`}
                                                    key={agent.id}
                                                    onClick={() => setSelectedAgentId(String(agent.id))}
                                                >
                                                    <strong>{agent.display_name}</strong>
                                                    <span>{agent.key}</span>
                                                    {agent.stage_key ? <small>{titleize(agent.stage_key)}</small> : null}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <SelectInput
                                    label="Version"
                                    value={selectedVersionId}
                                    onChange={setSelectedVersionId}
                                    options={versionOptions.length ? versionOptions : [['', 'No prompt versions']]}
                                    disabled={!versionOptions.length}
                                    wide
                                />
                            </div>
                            <div className="intake-prompt-selector-actions">
                                <StatusBadge value={selectedVersion?.status || 'inactive'} />
                                <AppButton icon="pi pi-plus" variant="secondary" label={createDraftLabel} onClick={createDraft} disabled={saving || !selectedAgent} title={createDraftTitle} />
                            </div>
                            {!canEditPrompt ? (
                                <div className="intake-admin-callout intake-admin-callout--locked intake-prompt-selector-callout">
                                    <div>
                                        <strong>{selectedVersion ? 'Read-only version' : 'Prompt draft needed'}</strong>
                                        <span>{promptLockMessage}</span>
                                    </div>
                                    {selectedAgent ? (
                                        <AppButton icon="pi pi-pencil" label="Create Editable Draft" disabled={saving} onClick={createDraft} />
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </aside>

                    <div className="intake-prompt-editor-column">
                        <AdminCard
                            title="Prompt Editor"
                            description={selectedAgent ? `Editing ${selectedAgent.display_name || selectedAgent.key}.` : 'Select an agent before editing.'}
                            actions={(
                                <>
                                    <UnsavedBadge visible={promptDirty} />
                                    <AppButton icon="pi pi-save" variant="secondary" label="Save Prompt Draft" onClick={savePrompt} disabled={saving || !canEditPrompt} title={canEditPrompt ? 'Save this prompt draft' : promptLockMessage} />
                                    <AppButton icon="pi pi-check" variant="success" label="Publish Draft" onClick={publishPrompt} disabled={saving || !canEditPrompt} title={canEditPrompt ? 'Publish this draft as the active prompt' : promptLockMessage} />
                                </>
                            )}>
                            <div className="intake-prompt-editor-fields">
                                <TextArea
                                    label="System Prompt"
                                    value={promptForm.system_prompt}
                                    rows={14}
                                    readOnly={!canEditPrompt}
                                    inputRef={systemPromptRef}
                                    title={!canEditPrompt ? 'Click to create an editable draft from this prompt.' : undefined}
                                    onClick={() => createDraftFromPromptField('system_prompt')}
                                    onFocus={() => createDraftFromPromptField('system_prompt')}
                                    onChange={(value) => setPromptForm((current) => ({ ...current, system_prompt: value }))}
                                />
                                <TextArea
                                    label="User Prompt Template"
                                    value={promptForm.user_prompt_template}
                                    rows={14}
                                    readOnly={!canEditPrompt}
                                    inputRef={userPromptRef}
                                    title={!canEditPrompt ? 'Click to create an editable draft from this prompt.' : undefined}
                                    onClick={() => createDraftFromPromptField('user_prompt_template')}
                                    onFocus={() => createDraftFromPromptField('user_prompt_template')}
                                    onChange={(value) => setPromptForm((current) => ({ ...current, user_prompt_template: value }))}
                                />
                                <TextInput label="Change Notes" value={promptForm.change_notes} disabled={!canEditPrompt} onChange={(value) => setPromptForm((current) => ({ ...current, change_notes: value }))} wide />
                            </div>
                        </AdminCard>

                        <div className="intake-prompt-secondary-stack">
                            <details className="intake-prompt-disclosure">
                                <summary>
                                    <span>Prompt health</span>
                                    <small>{promptHealthLabel}</small>
                                </summary>
                                <div className="intake-prompt-disclosure__body">
                                    <TemplateVariablePanel template={promptForm.user_prompt_template} schemaJson={promptForm.variables_schema_json} />
                                </div>
                            </details>

                            <details className="intake-prompt-disclosure">
                                <summary>
                                    <span>Version history</span>
                                    <small>{versions.length ? `${versions.length} version${versions.length === 1 ? '' : 's'}` : 'No versions'}</small>
                                </summary>
                                <div className="intake-prompt-disclosure__body">
                                    <div className="intake-prompt-disclosure__actions">
                                        <AppButton icon="pi pi-copy" variant="secondary" label="Create Draft from Selected" onClick={createDraftFromSelected} disabled={saving || !selectedVersion} />
                                        <AppButton icon="pi pi-box" variant="secondary" label="Archive Draft" onClick={archivePrompt} disabled={saving || !canArchivePrompt} title={canArchivePrompt ? 'Archive this draft' : promptLockMessage} />
                                    </div>
                                    {loadingVersions ? <AdminEmptyState title="Loading versions..." /> : (
                                        <div className="intake-record-list">
                                            {versions.map((version) => (
                                                <button
                                                    type="button"
                                                    className={`intake-draft-card${String(version.id) === String(selectedVersionId) ? ' intake-draft-card--selected' : ''}`}
                                                    key={version.id}
                                                    onClick={() => setSelectedVersionId(String(version.id))}
                                                >
                                                    <div className="intake-draft-card__main">
                                                        <strong>Version {version.version_number}</strong>
                                                        <span>{version.change_notes || 'No notes'}</span>
                                                        <small>{version.published_at ? `Published ${formatDate(version.published_at)}` : formatDate(version.updated_at || version.created_at)}</small>
                                                    </div>
                                                    <StatusBadge value={version.status} />
                                                </button>
                                            ))}
                                            {!versions.length && <AdminEmptyState title="No prompt versions" />}
                                        </div>
                                    )}
                                </div>
                            </details>

                            <details className="intake-prompt-disclosure">
                                <summary>
                                    <span>Agent settings</span>
                                    <small>{agentDirty ? 'Unsaved changes' : selectedAgent?.stage_key ? titleize(selectedAgent.stage_key) : 'No agent selected'}</small>
                                </summary>
                                <div className="intake-prompt-disclosure__body">
                                    <div className="intake-prompt-disclosure__actions">
                                        <UnsavedBadge visible={agentDirty} />
                                        <AppButton icon="pi pi-save" variant="secondary" label="Save Agent Settings" onClick={saveAgent} disabled={saving || !selectedAgent} />
                                    </div>
                                    <div className="intake-form-grid intake-form-grid--compact">
                                        <TextInput label="Display Name" value={agentForm.display_name} onChange={(value) => setAgentForm((current) => ({ ...current, display_name: value }))} />
                                        <SelectInput label="Stage" value={agentForm.stage_key} onChange={(value) => setAgentForm((current) => ({ ...current, stage_key: value }))} options={stageOptions} />
                                        <SelectInput label="Default Tier" value={agentForm.default_model_tier} onChange={(value) => setAgentForm((current) => ({ ...current, default_model_tier: value }))} options={MODEL_TIER_OPTIONS} />
                                        <CheckboxInput label="Active" checked={agentForm.active} onChange={(value) => setAgentForm((current) => ({ ...current, active: value }))} />
                                        <TextArea label="Description" value={agentForm.description} rows={3} onChange={(value) => setAgentForm((current) => ({ ...current, description: value }))} />
                                    </div>
                                </div>
                            </details>

                        <details className="intake-prompt-disclosure">
                            <summary>
                                <span>Advanced settings</span>
                                <small>Model, variables, output schema</small>
                            </summary>
                            <div className="intake-prompt-disclosure__body">
                                <div className="intake-form-grid">
                                    <SelectInput label="Model Tier" value={promptForm.model_tier} disabled={!canEditPrompt} onChange={(value) => setPromptForm((current) => ({ ...current, model_tier: value }))} options={MODEL_TIER_OPTIONS} />
                                    <TextInput label="Temperature" type="number" value={promptForm.model_settings.temperature} disabled={!canEditPrompt} onChange={(value) => updatePromptModelSetting('temperature', value)} />
                                    <TextInput label="Max Tokens" type="number" value={promptForm.model_settings.max_tokens} disabled={!canEditPrompt} onChange={(value) => updatePromptModelSetting('max_tokens', value)} />
                                    <SelectInput label="Reasoning Effort" value={promptForm.model_settings.reasoning_effort} disabled={!canEditPrompt} onChange={(value) => updatePromptModelSetting('reasoning_effort', value)} options={REASONING_EFFORT_OPTIONS} />
                                </div>
                                <details className="intake-prompt-advanced">
                                    <summary>Advanced Schemas</summary>
                                    <div className="intake-split-panel">
                                        <SchemaEditor title="Variables Schema" value={promptForm.variables_schema_json} disabled={!canEditPrompt} validationMessages={variableSchemaMessages} onChange={(value) => setPromptForm((current) => ({ ...current, variables_schema_json: value }))} />
                                        <SchemaEditor title="Output Validation Schema" value={promptForm.output_schema_json} disabled={!canEditPrompt} onChange={(value) => setPromptForm((current) => ({ ...current, output_schema_json: value }))} />
                                    </div>
                                </details>
                            </div>
                        </details>

                        <details className="intake-prompt-disclosure">
                            <summary>
                                <span>Preview and raw diff</span>
                                <small>{publishedVersion && selectedVersion ? 'Available' : 'Select a version'}</small>
                            </summary>
                            <div className="intake-prompt-disclosure__body intake-prompt-preview-grid">
                                <div>
                                    <PromptTextPanel
                                        title="Firm Context + Agent Prompt"
                                        version={selectedVersion}
                                        text={selectedVersion ? buildAssembledPromptText(publishedFirmContext, promptForm) : ''}
                                        emptyTitle="Select a prompt version"
                                    />
                                </div>
                                <div>
                                    <PromptDiff publishedVersion={publishedVersion} selectedVersion={selectedVersion} selectedForm={promptForm} />
                                </div>
                            </div>
                        </details>
                        </div>
                    </div>
                </div>
            ) : null}
        </AdminSection>
    );
}



function PipelineVersions({ agents, versions, currentPublished, onPipelineChanged }) {
    const showToast = useContext(ToastContext);
    const [selectedVersionId, setSelectedVersionId] = useState('');
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: '', description: '', reason: '' });
    const [stages, setStages] = useState([]);
    const [customStageLabel, setCustomStageLabel] = useState('');
    const [agentSelections, setAgentSelections] = useState({});

    const selectedSummary = versions.find((version) => String(version.id) === String(selectedVersionId));
    const canEdit = selectedVersion?.status === 'draft';
    const canArchive = selectedVersion && selectedVersion.status !== 'archived' && selectedVersion.status !== 'published';
    const currentSourceId = currentPublished?.id || versions[0]?.id;
    const versionLockMessage = !selectedVersion
        ? 'Select a pipeline version or create a draft.'
        : canEdit
            ? 'This draft can be edited. Save it before publishing.'
            : selectedVersion.status === 'published'
                ? 'Published pipeline versions are read-only so existing leads and run history stay stable. Create a draft to make changes.'
                : 'Archived pipeline versions are read-only. Create a draft from a published version to make changes.';
    const saveDraftDisabledReason = !selectedVersion
        ? 'Select or create a draft first.'
        : !canEdit
            ? 'Only draft pipeline versions can be saved.'
            : '';
    const publishDisabledReason = !selectedVersion
        ? 'Select or create a draft first.'
        : !canEdit
            ? 'Only draft pipeline versions can be published.'
            : !form.reason.trim()
                ? 'Enter a publish reason first.'
                : '';
    const archiveDisabledReason = !selectedVersion
        ? 'Select a draft first.'
        : !canArchive
            ? 'Published and archived versions cannot be archived from here.'
            : '';
    const lockedControlTitle = canEdit ? '' : versionLockMessage;
    const canSaveDraft = Boolean(selectedVersion && canEdit && !saving && !loadingDetail);
    const canPublishDraft = Boolean(selectedVersion && canEdit && form.reason.trim() && !saving && !loadingDetail);
    const canArchiveDraft = Boolean(canArchive && !saving && !loadingDetail);

    useEffect(() => {
        if (!selectedVersionId && versions.length) {
            setSelectedVersionId(String(versions[0].id));
        }
    }, [selectedVersionId, versions]);

    useEffect(

    () => {
        if (!selectedVersionId) {
            setSelectedVersion(null);
            setStages(parseStageConfig(currentPublished));
            return undefined;
        }
        let active = true;
        setLoadingDetail(true);
        fetchIntakePipelineVersion(selectedVersionId)
            .then((response) => {
                if (!active) return;
                setSelectedVersion(response.data.pipeline_version);
            })
            .catch((error) => {
                console.error('Failed to load intake pipeline version:', error);
                if (active) showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load pipeline version' });
            })
            .finally(() => {
                if (active) setLoadingDetail(false);
            });
        return () => {
            active = false;
        };
    }, [currentPublished, selectedVersionId, showToast]);

    useEffect(() => {
        if (!selectedVersion) return;
        setForm({
            name: selectedVersion.name || '',
            description: selectedVersion.description || '',
            reason: '',
        });
        setStages(normalizeStageDraft(parseStageConfig(selectedVersion)));
    }, [selectedVersion]);

    const changeStage =

    (index, updates) => {
        setStages((current) => current.map((stage, stageIndex) => (
            stageIndex === index ? { ...stage, ...updates } : stage
        )));
    };

    const moveStage =

    (index, direction) => {
        setStages(

        (current) => {
            const nextIndex = index + direction;
            if (nextIndex < 1 || nextIndex > current.length - 3) return current;
            const next = [...current];
            [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
            return normalizeStageDraft(next);
        });
    };

    const addCustomStage =

    () => {
        const label = customStageLabel.trim();
        const key = slugifyKey(label);
        if (!label || !key || stages.some((stage) => stage.key === key)) {
            showToast({ severity: 'warn', summary: 'Stage not added', detail: 'Use a unique custom stage name.' });
            return;
        }
        const decisionIndex = Math.max(1, stages.findIndex((stage) => stage.key === 'decision'));
        const next = [...stages];
        next.splice(decisionIndex, 0, {
            key,
            label,
            description: '',
            core: false,
            enabled: true,
            agents: [],
        });
        setStages(normalizeStageDraft(next));
        setCustomStageLabel('');
    };

    const removeCustomStage =

    (index) => {
        setStages((current) => normalizeStageDraft(current.filter((_, stageIndex) => stageIndex !== index)));
    };

    const buildAgentConfig =

    (agent) => ({
        agent_id: agent.id,
        agent_key: agent.key,
        display_name: agent.display_name,
        enabled: agent.active !== false,
        agent_role: agent.key === 'dedupe' ? 'dedupe' : agent.stage_key === 'triage' ? 'triage' : agent.stage_key === 'info_gathering' ? 'information_request' : agent.stage_key === 'document_review' ? 'document_review' : agent.stage_key === 'final_brief' ? 'final_eval' : 'specialist',
        prompt_version_id: agent.published_prompt_version?.id || null,
        prompt_version_number: agent.published_prompt_version?.version_number || null,
        model_tier_override: null,
    });

    const addAgentToStage =

    (stageKey) => {
        const agentId = agentSelections[stageKey];
        const agent = agents.find((item) => String(item.id) === String(agentId));
        if (!agent) return;
        setStages((current) => normalizeStageDraft(current.map((stage) => {
            if (stage.key !== stageKey || (stage.agents || []).some((item) => item.agent_key === agent.key)) return stage;
            return { ...stage, agents: (stage.agents || []).concat(buildAgentConfig(agent)) };
        })));
        setAgentSelections((current) => ({ ...current, [stageKey]: '' }));
    };

    const updateStageAgent =

    (stageKey, agentKey, updates) => {
        setStages((current) => normalizeStageDraft(current.map((stage) => {
            if (stage.key !== stageKey) return stage;
            return {
                ...stage,
                agents: (stage.agents || []).map((agent) => (
                    agent.agent_key === agentKey ? { ...agent, ...updates } : agent
                )),
            };
        })));
    };

    const moveStageAgent =

    (stageKey, agentKey, direction) => {
        setStages((current) => normalizeStageDraft(current.map(

        (stage) => {
            if (stage.key !== stageKey) return stage;
            const agentsForStage = [...(stage.agents || [])];
            const index = agentsForStage.findIndex((agent) => agent.agent_key === agentKey);
            const nextIndex = index + direction;
            if (index < 0 || nextIndex < 0 || nextIndex >= agentsForStage.length) return stage;
            [agentsForStage[index], agentsForStage[nextIndex]] = [agentsForStage[nextIndex], agentsForStage[index]];
            return { ...stage, agents: agentsForStage };
        })));
    };

    const removeStageAgent =

    (stageKey, agentKey) => {
        setStages((current) => normalizeStageDraft(current.map((stage) => {
            if (stage.key !== stageKey) return stage;
            return { ...stage, agents: (stage.agents || []).filter((agent) => agent.agent_key !== agentKey) };
        })));
    };

    const createDraftFrom =

    async (sourceVersionId, successDetail) => {
        setSaving(true);
        try {
            const response = await createIntakePipelineVersion(sourceVersionId ? { source_version_id: sourceVersionId } : {});
            const version = response.data.pipeline_version;
            setSelectedVersion(version);
            setSelectedVersionId(String(version.id));
            await onPipelineChanged();
            showToast({ severity: 'success', summary: 'Created', detail: successDetail });
        } catch (error) {
            console.error('Failed to create intake pipeline draft:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to create pipeline draft' });
        } finally {
            setSaving(false);
        }
    };

    const createDraft =

    async () => {
        await createDraftFrom(currentSourceId, 'Pipeline draft created');
    };

    const restoreAsDraft =

    async () => {
        if (!selectedVersion) return;
        await createDraftFrom(selectedVersion.id, 'Pipeline version restored as a new draft');
    };

    const saveDraft =

    async () => {
        if (!selectedVersion || !canEdit) return;
        setSaving(true);
        try {
            const response = await updateIntakePipelineVersion(selectedVersion.id, {
                name: form.name,
                description: form.description,
                stage_config_json: JSON.stringify(normalizeStageDraft(stages)),
            });
            setSelectedVersion(response.data.pipeline_version);
            await onPipelineChanged();
            showToast({ severity: 'success', summary: 'Saved', detail: 'Pipeline draft saved' });
        } catch (error) {
            console.error('Failed to save intake pipeline draft:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to save pipeline draft' });
        } finally {
            setSaving(false);
        }
    };

    const publishDraft =

    async () => {
        const reason = form.reason.trim();
        if (!selectedVersion || !reason) {
            showToast({ severity: 'warn', summary: 'Reason required', detail: 'Enter a publish reason.' });
            return;
        }
        setSaving(true);
        try {
            if (canEdit) {
                await updateIntakePipelineVersion(selectedVersion.id, {
                    name: form.name,
                    description: form.description,
                    stage_config_json: JSON.stringify(normalizeStageDraft(stages)),
                });
            }
            const response = await publishIntakePipelineVersion(selectedVersion.id, { reason });
            setSelectedVersion(response.data.pipeline_version);
            await onPipelineChanged();
            showToast({ severity: 'success', summary: 'Published', detail: 'Pipeline version published' });
        } catch (error) {
            console.error('Failed to publish intake pipeline version:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to publish pipeline version' });
        } finally {
            setSaving(false);
        }
    };

    const archiveDraft =

    async () => {
        if (!selectedVersion || !canArchive) return;
        setSaving(true);
        try {
            const response = await archiveIntakePipelineVersion(selectedVersion.id);
            setSelectedVersion(response.data.pipeline_version);
            await onPipelineChanged();
            showToast({ severity: 'success', summary: 'Archived', detail: 'Pipeline draft archived' });
        } catch (error) {
            console.error('Failed to archive intake pipeline version:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to archive pipeline version' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminSection>
            <div className="intake-split-panel">
                <AdminCard title="Versions" actions={(
                    <>
                        <AppButton icon="pi pi-plus" variant="secondary" label="New Draft" onClick={createDraft} disabled={saving} />
                        <AppButton icon="pi pi-refresh" variant="secondary" label="Restore as Draft" onClick={restoreAsDraft} disabled={saving || !selectedVersion} />
                    </>
                )}>
                    <div className="intake-record-list">
                        {versions.map((version) => (
                            <button
                                type="button"
                                className={`intake-draft-card${String(version.id) === String(selectedVersionId) ? ' intake-draft-card--selected' : ''}`}
                                key={version.id}
                                onClick={() => setSelectedVersionId(String(version.id))}
                            >
                                <div className="intake-draft-card__main">
                                    <strong>{version.name || `Pipeline v${version.version_number}`}</strong>
                                    <span>Version {version.version_number} / {version.stage_count || 0} stages</span>
                                    <small>{version.published_at ? `Published ${formatDate(version.published_at)}` : formatDate(version.updated_at || version.created_at)}</small>
                                </div>
                                <StatusBadge value={version.status} />
                            </button>
                        ))}
                        {!versions.length && <AdminEmptyState title="No pipeline versions" />}
                    </div>
                </AdminCard>

                <AdminCard
                    title="Version Editor"
                    description={selectedSummary ? `Version ${selectedSummary.version_number} is ${selectedSummary.status}. Existing leads stay pinned until upgraded.` : 'Create a draft to edit the pipeline.'}
                    actions={(
                        <>
                            <AppButton icon="pi pi-save" variant="secondary" label="Save Draft" onClick={saveDraft} disabled={!canSaveDraft} title={saveDraftDisabledReason || 'Save draft changes'} />
                            <AppButton icon="pi pi-check" variant="success" label="Publish" onClick={publishDraft} disabled={!canPublishDraft} title={publishDisabledReason || 'Publish this draft as the active pipeline'} />
                            <AppButton icon="pi pi-box" variant="secondary" label="Archive" onClick={archiveDraft} disabled={!canArchiveDraft} title={archiveDisabledReason || 'Archive this draft'} />
                        </>
                    )}
                >
                    {loadingDetail ? <AdminEmptyState title="Loading pipeline version..." /> : (
                        <>
                            <div className={`intake-admin-callout ${canEdit ? 'intake-admin-callout--draft' : 'intake-admin-callout--locked'}`}>
                                <strong>{canEdit ? 'Draft editing enabled' : 'Read-only version'}</strong>
                                <span>{versionLockMessage}</span>
                            </div>
                            <div className="intake-form-grid">
                                <TextInput label="Name" value={form.name} disabled={!canEdit} onChange={(value) => setForm((current) => ({ ...current, name: value }))} wide />
                                <TextArea label="Description" value={form.description} rows={3} disabled={!canEdit} onChange={(value) => setForm((current) => ({ ...current, description: value }))} />
                                <TextArea label="Publish Reason" value={form.reason} rows={3} disabled={!canEdit} onChange={(value) => setForm((current) => ({ ...current, reason: value }))} />
                            </div>
                            {publishDisabledReason ? <div className="intake-stage-action-empty">{publishDisabledReason}</div> : null}
                        </>
                    )}
                </AdminCard>
            </div>

            <AdminCard
                title="Orchestration Map"
                description="Read-only view of the selected pipeline version. Stage order runs left to right; agent dependencies appear under each stage."
            >
                <React.Suspense fallback={<AdminEmptyState title="Loading orchestration map..." />}>
                    <IntakePipelineOrchestrationMap stages={stages} />
                </React.Suspense>
                <div className="intake-orchestration-legend" aria-label="Orchestration map legend">
                    <span><i className="intake-orchestration-legend__stage" aria-hidden="true" />Stage order</span>
                    <span><i className="intake-orchestration-legend__agent" aria-hidden="true" />Stage to agent</span>
                    <span><i className="intake-orchestration-legend__dependency" aria-hidden="true" />Agent dependency</span>
                    <span><i className="intake-orchestration-legend__loop" aria-hidden="true" />Information request loop</span>
                    <span><i className="intake-orchestration-legend__exit" aria-hidden="true" />Reply/document exit</span>
                </div>
            </AdminCard>

            <AdminCard
                title="Pipeline Graph"
                description="Stages are sequential. Agents inside a stage are grouped by runtime role."
                actions={(
                    <div className="intake-action-row">
                        <input type="text" value={customStageLabel} disabled={!canEdit} title={lockedControlTitle || 'Name a custom stage'} placeholder={canEdit ? 'Custom stage name' : 'Create a draft to edit'} onChange={(event) => setCustomStageLabel(event.target.value)} />
                        <button type="button" className="intake-button" disabled={!canEdit || !customStageLabel.trim()} title={lockedControlTitle || 'Add this custom stage'} onClick={addCustomStage}>Add Stage</button>
                    </div>
                )}
            >
                {!canEdit ? <div className="intake-admin-callout intake-admin-callout--locked"><strong>Stage builder locked</strong><span>{versionLockMessage}</span></div> : null}
                <div className="intake-admin-stage-list">
                    {stages.map(

                    (stage, index) => {
                        const protectedStage = stage.key === 'ingest_dedupe' || stage.key === 'decision' || stage.key === 'conversion';
                        const availableAgents = agents.filter((agent) => !(stage.agents || []).some((assigned) => assigned.agent_key === agent.key));
                        const assignedAgents = stage.agents || [];
                        return (
                            <section className={`intake-admin-stage-card${stage.enabled === false ? ' intake-admin-stage-card--disabled' : ''}`} key={stage.key}>
                                <div className="intake-admin-stage-card__header">
                                    <div className="intake-admin-stage-card__title-block">
                                        <span className="intake-admin-stage-card__order">{index + 1}</span>
                                        <div>
                                            <h3>{stage.label || titleize(stage.key)}</h3>
                                            <div className="intake-admin-stage-card__meta">
                                                <span>{stage.key}</span>
                                                <span>{stage.core ? 'Core stage' : 'Custom stage'}</span>
                                                {protectedStage ? <span>Protected order</span> : null}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="intake-admin-stage-card__actions">
                                        <StatusBadge value={stage.enabled !== false ? 'active' : 'inactive'} />
                                        <button type="button" className="intake-button" disabled={!canEdit || protectedStage || index <= 1} title={!canEdit ? versionLockMessage : protectedStage ? 'Protected core stages cannot be moved.' : 'Move this stage up'} onClick={() => moveStage(index, -1)}>Up</button>
                                        <button type="button" className="intake-button" disabled={!canEdit || protectedStage || index >= stages.length - 3} title={!canEdit ? versionLockMessage : protectedStage ? 'Protected core stages cannot be moved.' : 'Move this stage down'} onClick={() => moveStage(index, 1)}>Down</button>
                                        {!stage.core ? <button type="button" className="intake-button" disabled={!canEdit} title={lockedControlTitle || 'Remove this custom stage'} onClick={() => removeCustomStage(index)}>Remove</button> : null}
                                    </div>
                                </div>

                                <div className="intake-admin-stage-card__body">
                                    <div className="intake-admin-stage-card__fields">
                                        <TextInput label="Label" value={stage.label} disabled={!canEdit} onChange={(value) => changeStage(index, { label: value })} />
                                        <TextInput label="Description" value={stage.description} disabled={!canEdit} onChange={(value) => changeStage(index, { description: value })} />
                                        <SelectInput label="Stage Type" value={stage.stage_type} disabled={!canEdit} options={STAGE_TYPE_OPTIONS} onChange={(value) => changeStage(index, { stage_type: value })} />
                                        <CheckboxInput label="Enabled" checked={stage.enabled !== false} disabled={!canEdit || protectedStage} onChange={(value) => changeStage(index, { enabled: protectedStage ? true : value })} />
                                        <CheckboxInput label="Can Request Review Gate" checked={stage.can_request_review_gate} disabled={!canEdit} onChange={(value) => changeStage(index, { can_request_review_gate: value })} />
                                    </div>

                                    <div className="intake-admin-stage-card__agents">
                                        <div className="intake-admin-stage-card__section-heading">
                                            <strong>Runtime Graph</strong>
                                            <span>{assignedAgents.length ? `${assignedAgents.filter((agent) => agent.enabled !== false).length} active / ${assignedAgents.length} assigned` : 'None assigned'}</span>
                                        </div>
                                        <StageRuntimeGraph agents={assignedAgents} />
                                        <div className="intake-admin-stage-card__section-heading intake-admin-stage-card__section-heading--assignment">
                                            <strong>Agent Assignment</strong>
                                            <span>Prompt, role, and display order</span>
                                        </div>
                                        <div className="intake-admin-agent-list">
                                            {assignedAgents.map((agent) => (
                                                <div className="intake-admin-agent-row" key={agent.agent_key}>
                                                    <div>
                                                        <strong>{agent.display_name || titleize(agent.agent_key)}</strong>
                                                        <span>{agent.prompt_version_number ? `Prompt v${agent.prompt_version_number}` : 'No published prompt'} / {titleize(agent.agent_role || 'specialist')}</span>
                                                    </div>
                                                    <div className="intake-admin-agent-row__actions">
                                                        <select value={agent.agent_role || 'specialist'} disabled={!canEdit} title={lockedControlTitle || 'Set this agent role in the orchestration graph'} onChange={(event) => updateStageAgent(stage.key, agent.agent_key, { agent_role: event.target.value })}>
                                                            {AGENT_ROLE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                                        </select>
                                                        <label className="intake-checkbox-row">
                                                            <input type="checkbox" checked={agent.enabled !== false} disabled={!canEdit} title={lockedControlTitle || 'Enable or disable this agent in the stage'} onChange={(event) => updateStageAgent(stage.key, agent.agent_key, { enabled: event.target.checked })} />
                                                            <span>Enabled</span>
                                                        </label>
                                                        <button type="button" className="intake-button" disabled={!canEdit} title={lockedControlTitle || 'Move this agent up in display order'} onClick={() => moveStageAgent(stage.key, agent.agent_key, -1)}>Display Up</button>
                                                        <button type="button" className="intake-button" disabled={!canEdit} title={lockedControlTitle || 'Move this agent down in display order'} onClick={() => moveStageAgent(stage.key, agent.agent_key, 1)}>Display Down</button>
                                                        <button type="button" className="intake-button" disabled={!canEdit} title={lockedControlTitle || 'Remove this agent from the stage'} onClick={() => removeStageAgent(stage.key, agent.agent_key)}>Remove</button>
                                                    </div>
                                                </div>
                                            ))}
                                            {!assignedAgents.length ? <div className="intake-stage-action-empty">No agents assigned.</div> : null}
                                        </div>
                                        <div className="intake-admin-agent-add">
                                            <select value={agentSelections[stage.key] || ''} disabled={!canEdit || !availableAgents.length} title={lockedControlTitle || (availableAgents.length ? 'Select an agent to add to this stage' : 'All agents are already assigned to this stage')} onChange={(event) => setAgentSelections((current) => ({ ...current, [stage.key]: event.target.value }))}>
                                                <option value="">{canEdit ? 'Select agent' : 'Create a draft to edit'}</option>
                                                {availableAgents.map((agent) => (
                                                    <option key={agent.id} value={agent.id}>{agent.display_name} ({agent.key})</option>
                                                ))}
                                            </select>
                                            <button type="button" className="intake-button" disabled={!canEdit || !agentSelections[stage.key]} title={lockedControlTitle || 'Add the selected agent to this stage'} onClick={() => addAgentToStage(stage.key)}>Add Agent</button>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        );
                    })}
                    {!stages.length && <AdminEmptyState title="No stage configuration loaded" />}
                </div>
            </AdminCard>
        </AdminSection>
    );
}



function PromptPreview({ title, version, promptTextField }) {
    const promptText = promptTextField ? version?.[promptTextField] : null;
    return (
        <div className="intake-stage-context">
            <div className="intake-stage-context__header">
                <h3>{title}</h3>
                {version ? <StatusBadge value={version.status} /> : null}
            </div>
            {version ? (
                <pre className="intake-draft-placeholder" style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                    {promptTextField ? [
                        `Version ${version.version_number}`,
                        '',
                        promptText || '',
                    ].join('\n') : [
                        `Version ${version.version_number}`,
                        '',
                        'System:',
                        version.system_prompt || '',
                        '',
                        'User:',
                        version.user_prompt_template || '',
                    ].join('\n')}
                </pre>
            ) : <AdminEmptyState title="No version" />}
        </div>
    );
}



function ModelCatalog() {
    return <AdminCard title="AI models" description="Choose models for Intake and the rest of the firm in one place.">
        <a className="app-button" href="/admin?tab=ai&view=features">Open AI control center →</a>
    </AdminCard>;
}



function StaffRoles({ roles, assignments, people, requiredRoleKeys, onRolesChanged }) {
    const showToast = useContext(ToastContext);
    const [roleForm, setRoleForm] = useState(EMPTY_ROLE_FORM);
    const [assignmentForm, setAssignmentForm] = useState(EMPTY_ASSIGNMENT_FORM);
    const [saving, setSaving] = useState(false);
    const roleOptions = roles.map((role) => [String(role.id), `${role.display_name} (${role.role_key})`]);
    const roleById = useMemo(() => roles.reduce((map, role) => ({ ...map, [role.id]: role }), {}), [roles]);

    useEffect(() => {
        if (!assignmentForm.role_id && roles[0]?.id) {
            setAssignmentForm((current) => ({ ...current, role_id: String(roles[0].id) }));
        }
    }, [assignmentForm.role_id, roles]);

    const saveRole =

    async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            if (roleForm.id) {
                await updateIntakeStaffRole(roleForm.id, roleForm);
            } else {
                await createIntakeStaffRole(roleForm);
            }
            setRoleForm(EMPTY_ROLE_FORM);
            onRolesChanged();
            showToast({ severity: 'success', summary: 'Saved', detail: 'Staff role saved' });
        } catch (error) {
            console.error('Failed to save intake staff role:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to save staff role' });
        } finally {
            setSaving(false);
        }
    };

    const saveAssignment =

    async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...assignmentForm,
                role_id: assignmentForm.role_id,
                priority: Number(assignmentForm.priority || 100),
            };
            if (assignmentForm.id) {
                await updateIntakeStaffRoleAssignment(assignmentForm.id, payload);
            } else {
                await createIntakeStaffRoleAssignment(payload);
            }
            setAssignmentForm({ ...EMPTY_ASSIGNMENT_FORM, role_id: assignmentForm.role_id });
            onRolesChanged();
            showToast({ severity: 'success', summary: 'Saved', detail: 'Role assignment saved' });
        } catch (error) {
            console.error('Failed to save intake role assignment:', error);
            showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to save role assignment' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminSection>
            <datalist id="intake-people-emails">
                {people.map((person) => <option key={person.id || person.email} value={person.email}>{person.name}</option>)}
            </datalist>

            <div className="intake-split-panel">
                <AdminCard title={roleForm.id ? 'Edit Staff Role' : 'Add Staff Role'}>
                    <form className="intake-edit-form" onSubmit={saveRole}>
                        <div className="intake-form-grid">
                            <TextInput label="Role Key" value={roleForm.role_key} onChange={(value) => setRoleForm((current) => ({ ...current, role_key: value }))} />
                            <TextInput label="Display Name" value={roleForm.display_name} onChange={(value) => setRoleForm((current) => ({ ...current, display_name: value }))} />
                            <TextArea label="Description" value={roleForm.description} rows={3} onChange={(value) => setRoleForm((current) => ({ ...current, description: value }))} />
                            <CheckboxInput label="Active" checked={roleForm.active} onChange={(value) => setRoleForm((current) => ({ ...current, active: value }))} />
                        </div>
                        <div className="intake-action-row">
                            <AppButton icon="pi pi-save" label="Save Role" type="submit" disabled={saving} />
                            {roleForm.id ? <AppButton icon="pi pi-times" variant="secondary" label="Cancel" onClick={() => setRoleForm(EMPTY_ROLE_FORM)} /> : null}
                        </div>
                    </form>
                </AdminCard>

                <AdminCard title={assignmentForm.id ? 'Edit Assignment' : 'Add Assignment'}>
                    <form className="intake-edit-form" onSubmit={saveAssignment}>
                        <div className="intake-form-grid">
                            <SelectInput label="Role" value={assignmentForm.role_id || (roles[0]?.id ? String(roles[0].id) : '')} onChange={(value) => setAssignmentForm((current) => ({ ...current, role_id: value }))} options={roleOptions} wide />
                            <TextInput label="Assignee Email" value={assignmentForm.assignee_email} list="intake-people-emails" onChange={(value) => setAssignmentForm((current) => ({ ...current, assignee_email: value }))} wide />
                            <SelectInput label="Type" value={assignmentForm.assignee_type} onChange={(value) => setAssignmentForm((current) => ({ ...current, assignee_type: value }))} options={ASSIGNEE_TYPES} />
                            <TextInput label="Priority" type="number" value={assignmentForm.priority} onChange={(value) => setAssignmentForm((current) => ({ ...current, priority: value }))} />
                            <TextInput label="Category Scope" value={assignmentForm.category} onChange={(value) => setAssignmentForm((current) => ({ ...current, category: value }))} />
                            <TextInput label="State Scope" value={assignmentForm.state} onChange={(value) => setAssignmentForm((current) => ({ ...current, state: value }))} />
                            <TextInput label="Source Scope" value={assignmentForm.source} onChange={(value) => setAssignmentForm((current) => ({ ...current, source: value }))} />
                            <CheckboxInput label="Primary" checked={assignmentForm.is_primary} onChange={(value) => setAssignmentForm((current) => ({ ...current, is_primary: value }))} />
                            <CheckboxInput label="Active" checked={assignmentForm.active} onChange={(value) => setAssignmentForm((current) => ({ ...current, active: value }))} />
                        </div>
                        <div className="intake-action-row">
                            <AppButton icon="pi pi-save" label="Save Assignment" type="submit" disabled={saving || !roles.length} />
                            {assignmentForm.id ? <AppButton icon="pi pi-times" variant="secondary" label="Cancel" onClick={() => setAssignmentForm(EMPTY_ASSIGNMENT_FORM)} /> : null}
                        </div>
                    </form>
                </AdminCard>
            </div>

            <AdminCard title="Role Registry">
                <div className="intake-record-list">
                    {roles.map((role) => (
                        <div className="intake-record-row" key={role.id}>
                            <div>
                                <strong>{role.display_name}</strong>
                                <span>{role.role_key}{requiredRoleKeys.includes(role.role_key) ? ' / required' : ''}</span>
                                <small>{role.description || formatDate(role.updated_at || role.created_at)}</small>
                            </div>
                            <div className="intake-record-row__actions">
                                <StatusBadge value={role.active} />
                                <button type="button" className="intake-button" onClick={() => setRoleForm(role)}>Edit</button>
                            </div>
                        </div>
                    ))}
                </div>
            </AdminCard>

            <AdminCard title="Assignments">
                <div className="intake-record-list">
                    {assignments.map((assignment) => (
                        <div className="intake-record-row" key={assignment.id}>
                            <div>
                                <strong>{assignment.assignee_email}</strong>
                                <span>{roleById[assignment.role_id]?.display_name || assignment.role_key} / {titleize(assignment.assignee_type)} / priority {assignment.priority}</span>
                                <small>{[assignment.category, assignment.state, assignment.source].filter(Boolean).join(' / ') || 'Global scope'}</small>
                            </div>
                            <div className="intake-record-row__actions">
                                {assignment.is_primary ? <span className="intake-badge intake-badge--success">Primary</span> : null}
                                <StatusBadge value={assignment.active} />
                                <button type="button" className="intake-button" onClick={() => setAssignmentForm({
                                    ...EMPTY_ASSIGNMENT_FORM,
                                    ...assignment,
                                    role_id: String(assignment.role_id),
                                })}>Edit</button>
                            </div>
                        </div>
                    ))}
                    {!assignments.length && <AdminEmptyState title="No role assignments" />}
                </div>
            </AdminCard>
        </AdminSection>
    );
}

export default IntakeAdmin;
